<template>
  <div class="entrepot-mobile" :style="mobileShellStyle">
    <header class="entrepot-mobile-header">
      <button
        type="button"
        class="entrepot-mobile-header__icon-btn"
        :aria-label="$t('back')"
        @click="goBack"
      >
        <v-icon size="24" color="#37474F">mdi-chevron-left</v-icon>
      </button>

      <h1 class="entrepot-mobile-header__title">
        {{ $t(entrepot.type) }}
      </h1>

      <div class="entrepot-mobile-header__actions">
        <button
          type="button"
          class="entrepot-mobile-header__icon-btn"
          :aria-label="$t('refresh')"
          :disabled="!entrepot.render || entrepot.isLoading"
          @click="onRefresh"
        >
          <v-icon size="22" color="#37474F">mdi-refresh</v-icon>
        </button>
        <button
          v-if="!entrepot.formType"
          type="button"
          class="entrepot-mobile-header__icon-btn"
          :aria-label="$t('list')"
          @click="goToList"
        >
          <v-icon size="22" color="#37474F">mdi-format-list-bulleted</v-icon>
        </button>
      </div>
    </header>

    <v-progress-linear
      :active="!entrepot.render"
      :indeterminate="!entrepot.render"
      absolute
      color="primary"
      style="top: 52px"
    />

    <div class="entrepot-mobile__body">
      <!-- Keep set-init defaults in sync with desktop. -->
      <div class="entrepot-mobile-init" aria-hidden="true">
        <autocomplete
          v-model="entrepot.entrepot"
          title="entrepot"
          type="entrepot"
          :set-init="!entrepot.id"
          :value="entrepot.entrepot"
        />
        <autocomplete
          v-model="entrepot.branch"
          :title="$t('branch')"
          type="branch"
          :set-init="!entrepot.id"
          :value="entrepot.branch"
        />
        <autocomplete
          v-if="isTransferType"
          v-model="entrepot.recipientEntrepot"
          title="recipientEntrepot"
          type="entrepot"
          :data="JSON.stringify({ withoutAccess: true })"
          :set-init="!entrepot.id"
        />
        <autocomplete
          v-if="isTransferType"
          v-model="entrepot.recipientBranch"
          title="recipientBranch"
          type="branch"
          :data="JSON.stringify({ withoutAccess: true })"
          :set-init="!entrepot.id"
          :value="entrepot.recipientBranch"
        />
      </div>

      <div v-if="!entrepot.render" class="entrepot-mobile-empty">
        {{ $t("gettingData...pleaseWaite") }}
      </div>

      <template v-else>
        <div class="entrepot-summary-row" dir="rtl">
          <div class="entrepot-summary-card entrepot-summary-card--primary">
            <button
              type="button"
              class="entrepot-summary-card__main"
              @click="openPrimaryPicker"
            >
              <span class="entrepot-summary-card__label">{{ primaryCardLabel }}</span>
              <span
                class="entrepot-summary-card__value"
                :class="{ 'is-placeholder': !primaryCardValue }"
              >
                {{ primaryCardValue || primaryCardPlaceholder }}
              </span>
            </button>
            <button
              type="button"
              class="entrepot-summary-card__side"
              :aria-label="primaryCardLabel"
              @click.stop="openPrimaryPicker"
            >
              <v-icon size="22" color="#78909C">mdi-warehouse</v-icon>
            </button>
          </div>

          <button
            type="button"
            class="entrepot-summary-card entrepot-summary-card--meta"
            @click="metaSheet = true"
          >
            <div class="entrepot-summary-card__top">
              <span class="entrepot-summary-card__label">{{ metaCardLabel }}</span>
              <v-icon size="16" color="#90A4AE">mdi-pencil-outline</v-icon>
            </div>
            <div
              class="entrepot-summary-card__value"
              :class="{ 'is-placeholder': !metaCardText }"
            >
              {{ metaCardText || $t("entrepot-mobile-meta-placeholder") }}
            </div>
          </button>
        </div>

        <div class="entrepot-product-add-row" dir="rtl">
          <button
            v-if="canCreateProduct"
            type="button"
            class="entrepot-summary-add-btn"
            :aria-label="$t('product')"
            @click="openNewProductDialog"
          >
            <v-icon size="22" color="#1565C0">mdi-package-variant-plus</v-icon>
          </button>
          <button type="button" class="entrepot-add-row-btn" @click="onAddRow">
            <v-icon size="18" color="#607D8B">mdi-plus</v-icon>
            <span class="entrepot-add-row-btn__text">{{ $t("addRow") }}</span>
          </button>
        </div>

        <div v-if="filledItems.length" class="entrepot-product-rows" dir="rtl">
          <article
            v-for="(item, index) in filledItems"
            :key="item.id || 'row-' + index"
            class="entrepot-product-card"
          >
            <button
              type="button"
              class="entrepot-product-card__top"
              @click="openRowDetail(item)"
            >
              <div class="entrepot-product-card__media">
                <img
                  v-if="productImageUrl(item)"
                  :src="productImageUrl(item)"
                  alt=""
                  class="entrepot-product-card__img"
                />
                <v-icon v-else size="28" color="#B0BEC5">mdi-image-outline</v-icon>
              </div>
              <div class="entrepot-product-card__info">
                <div class="entrepot-product-card__title">
                  {{ item.product.name }}
                </div>
                <div
                  class="entrepot-product-card__line entrepot-product-card__line--accent"
                >
                  {{ $t("unit") }}:
                  <span>{{ rowUnitLabel(item) }}</span>
                </div>
                <div class="entrepot-product-card__line">
                  {{ countLabel }}:
                  <span>{{ item.count != null && item.count !== "" ? item.count : 0 }}</span>
                </div>
                <div
                  v-if="item.product && item.product.stock"
                  class="entrepot-product-card__line"
                >
                  {{ $t("stock") }}:
                  <span>{{ $numberFormat(item.product.stock.count, 3) }}</span>
                </div>
                <div
                  v-if="showToProduct && item.to_product"
                  class="entrepot-product-card__line"
                >
                  {{ $t("toProduct") }}:
                  <span>{{ item.to_product.name }}</span>
                </div>
              </div>
            </button>

            <div class="entrepot-product-card__footer">
              <div class="entrepot-product-card__tools">
                <button
                  type="button"
                  class="entrepot-product-card__tool"
                  :aria-label="$t('remove')"
                  @click="onDeleteRow(item)"
                >
                  <v-icon size="22" color="#78909C">mdi-delete-outline</v-icon>
                </button>
                <button
                  type="button"
                  class="entrepot-product-card__tool"
                  :aria-label="$t('edit')"
                  @click="openRowDetail(item)"
                >
                  <v-icon size="22" color="#78909C">mdi-pencil-outline</v-icon>
                </button>
              </div>

              <div class="entrepot-product-card__count">
                <button
                  type="button"
                  class="entrepot-product-card__count-btn"
                  @click="bumpCount(item, 1)"
                >
                  <v-icon size="18" color="#607D8B">mdi-plus</v-icon>
                </button>
                <button
                  type="button"
                  class="entrepot-product-card__count-value"
                  @click="openRowNumpad('count', item)"
                >
                  {{ item.count != null && item.count !== "" ? item.count : 0 }}
                  <small>{{ rowUnitLabel(item) }}</small>
                </button>
                <button
                  type="button"
                  class="entrepot-product-card__count-btn"
                  @click="bumpCount(item, -1)"
                >
                  <v-icon size="18" color="#607D8B">mdi-minus</v-icon>
                </button>
              </div>
            </div>
          </article>
        </div>
      </template>
    </div>

    <footer class="entrepot-mobile-footer" dir="rtl">
      <div class="entrepot-mobile-footer__summary">
        <span>{{ $t("entrepot-mobile-footer-items", { count: itemCount }) }}</span>
      </div>
      <button
        type="button"
        class="entrepot-mobile-footer__btn entrepot-mobile-footer__btn--outline"
        :disabled="!entrepot.render || entrepot.isLoading"
        @click="openReviewSheet"
      >
        {{ $t("entrepot-mobile-review-and-submit") }}
      </button>
      <button
        type="button"
        class="entrepot-mobile-footer__more"
        :aria-label="$t('more')"
        @click="moreSheet = true"
      >
        <v-icon size="22" color="#455A64">mdi-dots-vertical</v-icon>
      </button>
    </footer>

    <!-- Meta sheet: invoice-style nav list (warehouse excluded — primary card) -->
    <v-bottom-sheet v-model="metaSheet">
      <v-card class="entrepot-edit-sheet">
        <div class="entrepot-edit-sheet__handle" />
        <div class="entrepot-edit-sheet__title entrepot-edit-sheet__title--solo">
          {{ metaCardLabel }}
        </div>
        <div class="entrepot-edit-sheet__body entrepot-meta-sheet__body">
          <div class="entrepot-meta-sheet__nav-list" dir="rtl">
            <button
              type="button"
              class="entrepot-meta-sheet__item"
              @click="openBranchPicker('branch')"
            >
              <v-icon class="entrepot-meta-sheet__icon" size="22" color="#5A6B85">
                mdi-source-branch
              </v-icon>
              <span class="entrepot-meta-sheet__texts">
                <span class="entrepot-meta-sheet__item-title">{{ $t("branch") }}</span>
                <span class="entrepot-meta-sheet__item-sub">
                  {{ entityLabel(entrepot.branch) || $t("entrepot-mobile-select-branch") }}
                </span>
              </span>
            </button>

            <button
              type="button"
              class="entrepot-meta-sheet__item"
              @click="openMetaSubSheet('metaDateSheet')"
            >
              <v-icon class="entrepot-meta-sheet__icon" size="22" color="#5A6B85">
                mdi-calendar
              </v-icon>
              <span class="entrepot-meta-sheet__texts">
                <span class="entrepot-meta-sheet__item-title">{{ $t("date") }}</span>
                <span class="entrepot-meta-sheet__item-sub">
                  {{ entrepot.date || $t("entrepot-mobile-select-date") }}
                </span>
              </span>
            </button>

            <button
              type="button"
              class="entrepot-meta-sheet__item"
              @click="openMetaSubSheet('metaStatusTagSheet')"
            >
              <v-icon class="entrepot-meta-sheet__icon" size="22" color="#5A6B85">
                mdi-flag-outline
              </v-icon>
              <span class="entrepot-meta-sheet__texts">
                <span class="entrepot-meta-sheet__item-title">
                  {{ $t("entrepot-mobile-status-tag-title") }}
                </span>
                <span class="entrepot-meta-sheet__item-sub">
                  {{ statusTagSubtitle }}
                </span>
              </span>
            </button>

            <button
              v-if="showRelations"
              type="button"
              class="entrepot-meta-sheet__item"
              @click="openRelationsPicker"
            >
              <v-icon class="entrepot-meta-sheet__icon" size="22" color="#5A6B85">
                mdi-file-document-outline
              </v-icon>
              <span class="entrepot-meta-sheet__texts">
                <span class="entrepot-meta-sheet__item-title">{{
                  $t("relative-document")
                }}</span>
                <span class="entrepot-meta-sheet__item-sub">
                  {{ relationsSubtitle }}
                </span>
              </span>
            </button>

            <button
              v-if="isTransferType"
              type="button"
              class="entrepot-meta-sheet__item"
              @click="openBranchPicker('recipientBranch')"
            >
              <v-icon class="entrepot-meta-sheet__icon" size="22" color="#5A6B85">
                mdi-source-branch
              </v-icon>
              <span class="entrepot-meta-sheet__texts">
                <span class="entrepot-meta-sheet__item-title">{{
                  $t("recipientBranch")
                }}</span>
                <span class="entrepot-meta-sheet__item-sub">
                  {{
                    entityLabel(entrepot.recipientBranch) ||
                    $t("entrepot-mobile-select-branch")
                  }}
                </span>
              </span>
            </button>

            <button
              v-if="showDetail"
              type="button"
              class="entrepot-meta-sheet__item"
              @click="openDetailPicker"
            >
              <v-icon class="entrepot-meta-sheet__icon" size="22" color="#5A6B85">
                mdi-layers-outline
              </v-icon>
              <span class="entrepot-meta-sheet__texts">
                <span class="entrepot-meta-sheet__item-title">{{ $t("detail") }}</span>
                <span class="entrepot-meta-sheet__item-sub">
                  {{ entityLabel(entrepot.detail) || $t("entrepot-mobile-select-detail") }}
                </span>
              </span>
            </button>

            <button
              type="button"
              class="entrepot-meta-sheet__item"
              @click="openMetaSubSheet('metaContentSheet')"
            >
              <v-icon class="entrepot-meta-sheet__icon" size="22" color="#5A6B85">
                mdi-text
              </v-icon>
              <span class="entrepot-meta-sheet__texts">
                <span class="entrepot-meta-sheet__item-title">
                  {{ $t("entrepot-mobile-content-title") }}
                </span>
                <span class="entrepot-meta-sheet__item-sub">
                  {{ contentSubtitle }}
                </span>
              </span>
            </button>

            <button
              v-if="entrepot.documentPermmission"
              type="button"
              class="entrepot-meta-sheet__item"
              @click="openMetaDocuments"
            >
              <v-icon class="entrepot-meta-sheet__icon" size="22" color="#5A6B85">
                mdi-paperclip
              </v-icon>
              <span class="entrepot-meta-sheet__texts">
                <span class="entrepot-meta-sheet__item-title">
                  {{ $t("attachments") }} {{ $t("documents") }}
                </span>
                <span class="entrepot-meta-sheet__item-sub">
                  {{ $t("entrepot-mobile-documents-subtitle") }}
                </span>
              </span>
            </button>
          </div>
        </div>
        <div class="entrepot-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            height="48"
            class="entrepot-edit-sheet__submit"
            @click="metaSheet = false"
          >
            {{ $t("submit") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="metaDateSheet">
      <v-card class="entrepot-edit-sheet">
        <div class="entrepot-edit-sheet__handle" />
        <div class="entrepot-edit-sheet__header" dir="rtl">
          <span class="entrepot-edit-sheet__spacer" />
          <div class="entrepot-edit-sheet__title">{{ $t("date") }}</div>
          <button
            type="button"
            class="entrepot-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="backToMetaSheet"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="entrepot-edit-sheet__body" dir="rtl">
          <div class="entrepot-mobile-date">
            <div class="entrepot-mobile-date__selected">
              <span class="entrepot-mobile-date__label">{{ $t("date") }}</span>
              <strong class="entrepot-mobile-date__value">
                {{ entrepot.date || "—" }}
              </strong>
            </div>
            <date-picker
              v-model="entrepot.date"
              inline
              simple
              auto-submit
              :locale="$store.state.date_locale"
              :format="$store.state.date_format"
              :min="$store.state.startDate"
              :max="$store.state.endDate"
              class="entrepot-mobile-date__picker"
              color="#7B8BA3"
              @close="onMetaDateClose"
            />
          </div>
        </div>
        <div class="entrepot-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            height="48"
            class="entrepot-edit-sheet__submit"
            @click="backToMetaSheet"
          >
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="metaStatusTagSheet">
      <v-card class="entrepot-edit-sheet entrepot-status-tag-sheet">
        <div class="entrepot-edit-sheet__handle" />
        <div class="entrepot-edit-sheet__header" dir="rtl">
          <span class="entrepot-edit-sheet__spacer" />
          <div class="entrepot-edit-sheet__title">
            {{ $t("entrepot-mobile-status-tag-title") }}
          </div>
          <button
            type="button"
            class="entrepot-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="backToMetaSheet"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div
          class="entrepot-edit-sheet__body entrepot-status-tag-sheet__body"
          dir="rtl"
        >
          <div class="entrepot-edit-sheet__fields">
            <div class="entrepot-edit-field-wrap">
              <div class="entrepot-edit-field-wrap__label">{{ $t("status") }}</div>
              <status
                v-model="entrepot.status"
                :hideconfig="true"
                :type="entrepot.type"
                :value="entrepot.status"
                class="entrepot-status-tag-sheet__field entrepot-status-tag-sheet__status"
              />
            </div>
            <div class="entrepot-edit-field-wrap">
              <div class="entrepot-edit-field-wrap__label">{{ $t("tag") }}</div>
              <tag
                v-model="entrepot.tag"
                :solo="true"
                :input="entrepot.tag"
                :height="40"
                class="entrepot-status-tag-sheet__field entrepot-status-tag-sheet__tag"
              />
            </div>
          </div>
        </div>
        <div class="entrepot-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            height="48"
            class="entrepot-edit-sheet__submit"
            @click="backToMetaSheet"
          >
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="metaContentSheet">
      <v-card class="entrepot-edit-sheet">
        <div class="entrepot-edit-sheet__handle" />
        <div class="entrepot-edit-sheet__header" dir="rtl">
          <span class="entrepot-edit-sheet__spacer" />
          <div class="entrepot-edit-sheet__title">
            {{ $t("entrepot-mobile-content-title") }}
          </div>
          <button
            type="button"
            class="entrepot-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="backToMetaSheet"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="entrepot-edit-sheet__body" dir="rtl">
          <div class="entrepot-edit-field-wrap">
            <div class="entrepot-edit-field-wrap__label">
              {{ $t("description") }}
            </div>
            <textarea
              v-model="entrepot.content"
              class="entrepot-content-sheet__textarea"
              rows="5"
              :placeholder="$t('entrepot-mobile-content-title')"
            />
          </div>
        </div>
        <div class="entrepot-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            height="48"
            class="entrepot-edit-sheet__submit"
            @click="backToMetaSheet"
          >
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="expireDateSheet">
      <v-card class="entrepot-edit-sheet">
        <div class="entrepot-edit-sheet__handle" />
        <div class="entrepot-edit-sheet__header" dir="rtl">
          <span class="entrepot-edit-sheet__spacer" />
          <div class="entrepot-edit-sheet__title">{{ $t("expireDate") }}</div>
          <button
            type="button"
            class="entrepot-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="expireDateSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="entrepot-edit-sheet__body" dir="rtl">
          <div class="entrepot-mobile-date">
            <div class="entrepot-mobile-date__selected">
              <span class="entrepot-mobile-date__label">{{ $t("expireDate") }}</span>
              <strong class="entrepot-mobile-date__value">
                {{ (editItem && editItem.expire_date) || "—" }}
              </strong>
            </div>
            <date-picker
              v-if="editItem && entrepot.type === 'receipt'"
              v-model="editItem.expire_date"
              inline
              simple
              auto-submit
              :locale="$store.state.date_locale"
              :format="$store.state.date_format"
              class="entrepot-mobile-date__picker"
              color="#7B8BA3"
            />
          </div>
        </div>
        <div class="entrepot-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            height="48"
            class="entrepot-edit-sheet__submit"
            @click="expireDateSheet = false"
          >
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet
      v-model="rowNumpadOpen"
      class="entrepot-row-numpad-sheet"
      @click:outside="cancelRowNumpad"
    >
      <v-card class="entrepot-row-numpad" dir="rtl">
        <div class="entrepot-edit-sheet__handle" />
        <div class="entrepot-row-numpad__header">
          <span class="entrepot-row-numpad__header-spacer" />
          <div class="entrepot-row-numpad__title">{{ rowNumpadLabel }}</div>
          <button
            type="button"
            class="entrepot-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="cancelRowNumpad"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="entrepot-row-numpad__display">
          <div class="entrepot-row-numpad__display-label">{{ rowNumpadLabel }}</div>
          <div class="entrepot-row-numpad__display-value">{{ rowNumpadDisplay }}</div>
        </div>
        <div class="entrepot-row-numpad__pad" dir="ltr">
          <div class="entrepot-row-numpad__digits">
            <button
              v-for="n in [7, 8, 9, 4, 5, 6, 1, 2, 3]"
              :key="'d-' + n"
              type="button"
              class="entrepot-row-numpad__key"
              @click="rowNumpadDigit(n)"
            >
              {{ n }}
            </button>
            <button
              type="button"
              class="entrepot-row-numpad__key entrepot-row-numpad__key--multi"
              @click="rowNumpadAppend('00')"
            >
              00
            </button>
            <button
              type="button"
              class="entrepot-row-numpad__key"
              @click="rowNumpadDigit(0)"
            >
              0
            </button>
            <button
              type="button"
              class="entrepot-row-numpad__key entrepot-row-numpad__key--multi"
              @click="rowNumpadAppend('000')"
            >
              000
            </button>
          </div>
          <div class="entrepot-row-numpad__actions">
            <button
              type="button"
              class="entrepot-row-numpad__key entrepot-row-numpad__key--action"
              @click="rowNumpadStep(1)"
            >
              +
            </button>
            <button
              type="button"
              class="entrepot-row-numpad__key entrepot-row-numpad__key--action"
              @click="rowNumpadStep(-1)"
            >
              −
            </button>
            <button
              type="button"
              class="entrepot-row-numpad__key entrepot-row-numpad__key--action"
              @click="rowNumpadClear"
            >
              C
            </button>
            <button
              type="button"
              class="entrepot-row-numpad__key entrepot-row-numpad__key--action"
              @click="rowNumpadDot"
            >
              .
            </button>
            <button
              type="button"
              class="entrepot-row-numpad__key entrepot-row-numpad__key--action"
              @click="rowNumpadBack"
            >
              <v-icon size="20" color="#5A6B85">mdi-backspace-outline</v-icon>
            </button>
          </div>
        </div>
        <div class="entrepot-row-numpad__footer">
          <button
            type="button"
            class="entrepot-row-numpad__confirm"
            @click="confirmRowNumpad"
          >
            {{ $t("confirm") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Row detail sheet (invoice-style) -->
    <v-bottom-sheet v-model="editSheet">
      <v-card v-if="editItem" class="entrepot-row-detail">
        <div class="entrepot-edit-sheet__handle" />
        <div class="entrepot-row-detail__title">
          {{ $t("entrepot-mobile-row-detail") }}
        </div>

        <div class="entrepot-row-detail__product" dir="rtl">
          <div class="entrepot-row-detail__media">
            <img
              v-if="productImageUrl(editItem)"
              :src="productImageUrl(editItem)"
              alt=""
              class="entrepot-row-detail__img"
            />
            <v-icon v-else size="28" color="#B0BEC5">mdi-image-outline</v-icon>
          </div>
          <div class="entrepot-row-detail__product-text">
            <div class="entrepot-row-detail__name">
              {{
                editItem.product && editItem.product.name
                  ? editItem.product.name
                  : $t("entrepot-mobile-select-product")
              }}
            </div>
            <div
              v-if="editItem.product && editItem.product.stock"
              class="entrepot-row-detail__stock"
            >
              {{ $t("stock") }}:
              <span>
                {{ $numberFormat(editItem.product.stock.count, 3) }}
              </span>
            </div>
          </div>
          <button
            type="button"
            class="entrepot-row-detail__change"
            :aria-label="productFieldTitle"
            @click="changeRowProductFromDetail"
          >
            <v-icon size="18" color="#607D8B">mdi-swap-horizontal</v-icon>
          </button>
        </div>

        <div class="entrepot-row-detail__body" dir="rtl">
          <div v-if="showToProduct" class="entrepot-edit-field-wrap">
            <div class="entrepot-edit-field-wrap__label">{{ $t("toProduct") }}</div>
            <button
              type="button"
              class="entrepot-edit-picker-field"
              @click="openToProductPicker"
            >
              <span class="entrepot-edit-picker-field__text">
                {{
                  (editItem.to_product && editItem.to_product.name) ||
                  $t("entrepot-mobile-select-product")
                }}
              </span>
              <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
            </button>
          </div>

          <div v-if="showCountingFields" class="entrepot-edit-field-wrap">
            <div class="entrepot-edit-field-wrap__label">{{ $t("counting_one") }}</div>
            <button
              type="button"
              class="entrepot-row-detail__input-row"
              :disabled="isAltUnit(editItem)"
              @click="openRowNumpad('counting_one', editItem)"
            >
              <span class="entrepot-row-detail__input entrepot-row-detail__input--static">
                {{ editItem.counting_one != null ? editItem.counting_one : 0 }}
              </span>
            </button>
          </div>
          <div v-if="showCountingFields" class="entrepot-edit-field-wrap">
            <div class="entrepot-edit-field-wrap__label">{{ $t("counting_two") }}</div>
            <button
              type="button"
              class="entrepot-row-detail__input-row"
              :disabled="isAltUnit(editItem)"
              @click="openRowNumpad('counting_two', editItem)"
            >
              <span class="entrepot-row-detail__input entrepot-row-detail__input--static">
                {{ editItem.counting_two != null ? editItem.counting_two : 0 }}
              </span>
            </button>
          </div>

          <div v-show="isAltUnit(editItem)" class="entrepot-edit-field-wrap">
            <div class="entrepot-edit-field-wrap__label">{{ $t("count") }}</div>
            <button
              type="button"
              class="entrepot-row-detail__input-row"
              @click="openRowNumpad('countUnit', editItem)"
            >
              <span class="entrepot-row-detail__input entrepot-row-detail__input--static">
                {{ editItem.countUnit != null ? editItem.countUnit : 0 }}
              </span>
            </button>
          </div>

          <div
            v-if="entrepot.rowHasSerial(editItem)"
            class="entrepot-edit-field-wrap"
          >
            <div class="entrepot-edit-field-wrap__label">{{ $t("serial") }}</div>
            <button
              type="button"
              class="entrepot-edit-picker-field"
              @click="openRowSerialDialog"
            >
              <span class="entrepot-edit-picker-field__text">
                {{ rowSerialLabel(editItem) }}
              </span>
              <v-icon size="20" color="#78909C">mdi-barcode</v-icon>
            </button>
          </div>

          <div class="entrepot-edit-field-wrap">
            <div class="entrepot-edit-field-wrap__label">{{ countLabel }}</div>
            <button
              type="button"
              class="entrepot-row-detail__input-row"
              :disabled="isAltUnit(editItem)"
              @click="openRowNumpad('count', editItem)"
            >
              <span class="entrepot-row-detail__input entrepot-row-detail__input--static">
                {{ editItem.count != null && editItem.count !== "" ? editItem.count : 0 }}
              </span>
              <span class="entrepot-row-detail__affix">
                {{ rowUnitLabel(editItem) }}
              </span>
            </button>
          </div>

          <div v-if="showPriceField" class="entrepot-edit-field-wrap">
            <div class="entrepot-edit-field-wrap__label">{{ $t("price") }}</div>
            <button
              type="button"
              class="entrepot-row-detail__input-row"
              @click="openRowNumpad('price', editItem)"
            >
              <span class="entrepot-row-detail__input entrepot-row-detail__input--static">
                {{ editItem.price != null && editItem.price !== "" ? editItem.price : 0 }}
              </span>
            </button>
          </div>

          <div class="entrepot-edit-field-wrap">
            <div class="entrepot-edit-field-wrap__label">{{ $t("unit") }}</div>
            <v-menu
              v-model="rowUnitMenuOpen"
              offset-y
              left
              :disabled="
                entrepot.type == 'warehouseHandling' ||
                unitItems(editItem).length <= 1
              "
            >
              <template v-slot:activator="{ on, attrs }">
                <button
                  type="button"
                  class="entrepot-edit-picker-field"
                  :disabled="entrepot.type == 'warehouseHandling'"
                  v-bind="attrs"
                  v-on="on"
                >
                  <span class="entrepot-edit-picker-field__text">
                    {{ rowUnitLabel(editItem) }}
                  </span>
                  <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
                </button>
              </template>
              <v-list dense>
                <v-list-item
                  v-for="(unit, uIndex) in unitItems(editItem)"
                  :key="unit.id || uIndex"
                  @click="selectRowUnit(unit)"
                >
                  <v-list-item-title>{{ unit.fame || unit.name }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>

          <div v-if="showExpireDate" class="entrepot-edit-field-wrap">
            <div class="entrepot-edit-field-wrap__label">{{ $t("expireDate") }}</div>
            <button
              type="button"
              class="entrepot-edit-picker-field"
              :disabled="entrepot.type !== 'receipt'"
              @click="expireDateSheet = true"
            >
              <span class="entrepot-edit-picker-field__text">
                {{ editItem.expire_date || "—" }}
              </span>
              <v-icon size="20" color="#78909C">mdi-calendar</v-icon>
            </button>
          </div>

          <div v-if="showCostActions" class="entrepot-edit-field-wrap">
            <div class="entrepot-edit-field-wrap__label">{{ $t("costs") }}</div>
            <button
              type="button"
              class="entrepot-edit-picker-field"
              @click="entrepot.openCostListDialog(editItem)"
            >
              <span class="entrepot-edit-picker-field__text">
                {{ costLabel(editItem) }}
              </span>
              <v-icon size="20" color="#78909C">mdi-cash-minus</v-icon>
            </button>
            <button
              type="button"
              class="entrepot-cost-add-btn"
              @click="entrepot.openCostDialog(editItem)"
            >
              {{ $t("cost") }}
            </button>
          </div>

          <div class="entrepot-edit-field-wrap">
            <div class="entrepot-edit-field-wrap__label">{{ $t("entrepot") }}</div>
            <button
              type="button"
              class="entrepot-edit-picker-field"
              @click="openRowEntrepotPicker"
            >
              <span class="entrepot-edit-picker-field__text">
                {{
                  entityLabel(editItem.article_entrepot) ||
                  entityLabel(entrepot.entrepot) ||
                  "—"
                }}
              </span>
              <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
            </button>
          </div>

          <div class="entrepot-edit-field-wrap">
            <div class="entrepot-edit-field-wrap__label">
              {{ $t("description") }}
            </div>
            <textarea
              v-model="editItem.content"
              class="entrepot-row-detail__textarea"
              rows="4"
              :placeholder="$t('description')"
            />
          </div>
        </div>

        <div class="entrepot-row-detail__summary" dir="rtl">
          <span>
            {{ editItem.count || 0 }}
            {{ rowUnitLabel(editItem) }}
          </span>
          <template v-if="showPriceField">
            <span class="entrepot-row-detail__summary-sep">|</span>
            <span>{{ editItem.price || 0 }}</span>
          </template>
        </div>

        <div class="entrepot-row-detail__footer">
          <v-btn
            color="primary"
            block
            depressed
            height="48"
            class="entrepot-edit-sheet__submit"
            @click="editSheet = false"
          >
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="rowSerialSheet" persistent>
      <v-card class="entrepot-edit-sheet entrepot-row-serial-sheet">
        <div class="entrepot-edit-sheet__handle" />
        <div class="entrepot-edit-sheet__header" dir="rtl">
          <span class="entrepot-edit-sheet__spacer" />
          <div class="entrepot-edit-sheet__title">{{ $t("serial-management") }}</div>
          <button
            type="button"
            class="entrepot-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="closeRowSerialSheet"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div
          class="entrepot-edit-sheet__body entrepot-row-serial-sheet__body"
          dir="rtl"
        >
          <div v-if="rowSerialIsInput" class="entrepot-row-serial-sheet__add">
            <div class="entrepot-row-serial-sheet__add-row">
              <input
                ref="rowSerialInput"
                v-model="rowSerialInput"
                type="text"
                class="entrepot-row-serial-sheet__input"
                :placeholder="$t('enter-or-scan-serial')"
                @keydown.enter.prevent="addRowSerial"
              />
              <button
                type="button"
                class="entrepot-row-serial-sheet__add-btn"
                @click="addRowSerial"
              >
                <v-icon size="18" color="#fff">mdi-plus</v-icon>
                <span>{{ $t("add") }}</span>
              </button>
            </div>
            <p v-if="rowSerialDuplicateHint" class="entrepot-row-serial-sheet__hint">
              {{ $t("invoice-mobile-serial-duplicate") }}
            </p>
          </div>

          <div class="entrepot-row-serial-sheet__toolbar">
            <div class="entrepot-row-serial-sheet__search">
              <v-icon size="18" color="#90A4AE">mdi-magnify</v-icon>
              <input
                v-model="rowSerialSearch"
                type="search"
                class="entrepot-row-serial-sheet__search-input"
                :placeholder="
                  rowSerialIsOutput
                    ? $t('invoice-mobile-serial-search-output-placeholder')
                    : $t('search-in-serials')
                "
              />
            </div>
            <div
              v-if="rowSerialIsOutput && rowSerialVisibleList.length"
              class="entrepot-row-serial-sheet__tools"
            >
              <button
                type="button"
                class="entrepot-row-serial-sheet__link-btn"
                @click="toggleSelectAllRowSerials"
              >
                {{
                  rowSerialAllVisibleSelected
                    ? $t("invoice-mobile-deselect-all")
                    : $t("invoice-mobile-select-all")
                }}
              </button>
              <span class="entrepot-row-serial-sheet__count">
                {{ rowSerialEffectiveCount }} / {{ rowSerialAvailableCount }}
              </span>
              <button
                type="button"
                class="entrepot-row-serial-sheet__link-btn"
                :class="{ 'is-muted': !rowSerialEffectiveCount }"
                :disabled="!rowSerialEffectiveCount"
                @click="clearRowSerialSelection"
              >
                {{ $t("clear") }}
              </button>
            </div>
          </div>

          <div class="entrepot-row-serial-sheet__list">
            <div
              v-if="rowSerialLoading && !rowSerialVisibleList.length"
              class="entrepot-row-serial-sheet__empty"
            >
              <v-progress-circular
                indeterminate
                size="26"
                width="2"
                color="#1976D2"
                class="mb-2"
              />
              <span>{{ $t("invoice-mobile-loading-serials") }}</span>
            </div>
            <div
              v-else-if="!rowSerialVisibleList.length"
              class="entrepot-row-serial-sheet__empty"
            >
              <strong>{{ rowSerialEmptyTitle }}</strong>
              <span>{{ rowSerialEmptyHint }}</span>
            </div>

            <div
              v-for="(item, index) in rowSerialVisibleList"
              :key="item.uid || item.id || index"
              class="entrepot-row-serial-sheet__item"
              :class="{
                'is-selected': rowSerialIsOutput && item.selected,
                'is-pickable': rowSerialIsOutput,
                'is-input-row': rowSerialIsInput,
              }"
              role="button"
              tabindex="0"
              @click="onRowSerialItemClick(item)"
              @keydown.enter.prevent="onRowSerialItemClick(item)"
              @keydown.space.prevent="onRowSerialItemClick(item)"
            >
              <template v-if="rowSerialIsOutput">
                <span class="entrepot-row-serial-sheet__side" aria-hidden="true">
                  <span class="entrepot-row-serial-sheet__mark">
                    <span class="entrepot-row-serial-sheet__mark-dot" />
                  </span>
                </span>
                <span class="entrepot-row-serial-sheet__item-main">
                  <strong>{{ item.value }}</strong>
                  <small v-if="item.created_at">{{ item.created_at }}</small>
                </span>
                <span class="entrepot-row-serial-sheet__side" aria-hidden="true" />
              </template>
              <template v-else>
                <span class="entrepot-row-serial-sheet__item-index">
                  {{ index + 1 }}
                </span>
                <span
                  class="entrepot-row-serial-sheet__item-main entrepot-row-serial-sheet__item-main--start"
                >
                  <strong>{{ item.value }}</strong>
                  <small v-if="item.created_at">{{ item.created_at }}</small>
                </span>
                <button
                  type="button"
                  class="entrepot-row-serial-sheet__remove"
                  :aria-label="$t('remove')"
                  @click.stop="removeRowSerial(item)"
                >
                  <v-icon size="18" color="#E91E63">mdi-trash-can-outline</v-icon>
                </button>
              </template>
            </div>

            <button
              v-if="rowSerialIsOutput && rowSerialHasMore"
              type="button"
              class="entrepot-row-serial-sheet__more"
              :disabled="rowSerialLoading"
              @click="loadMoreRowSerials"
            >
              <v-progress-circular
                v-if="rowSerialLoading"
                indeterminate
                size="16"
                width="2"
                color="#1976D2"
              />
              <span v-else>{{ $t("invoice-mobile-load-more") }}</span>
            </button>
          </div>
        </div>

        <div class="entrepot-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            height="48"
            class="entrepot-edit-sheet__submit"
            @click="confirmRowSerialSheet"
          >
            {{ rowSerialConfirmLabel }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Review + submit -->
    <v-bottom-sheet v-model="reviewSheet">
      <v-card class="entrepot-edit-sheet">
        <div class="entrepot-edit-sheet__handle" />
        <div class="entrepot-edit-sheet__header" dir="rtl">
          <span class="entrepot-edit-sheet__spacer" />
          <div class="entrepot-edit-sheet__title">
            {{ $t("entrepot-mobile-review-and-submit") }}
          </div>
          <button
            type="button"
            class="entrepot-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="reviewSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="entrepot-edit-sheet__body" dir="rtl">
          <p class="entrepot-review__hint">{{ $t("entrepot-mobile-review-hint") }}</p>
          <div class="entrepot-review__card">
            <div class="entrepot-review__row">
              <span>{{ metaCardLabel }}</span>
              <strong>{{ metaCardText || "—" }}</strong>
            </div>
            <div class="entrepot-review__row">
              <span>{{ primaryCardLabel }}</span>
              <strong>{{ primaryCardValue || "—" }}</strong>
            </div>
            <div class="entrepot-review__row">
              <span>{{ $t("entrepot-mobile-footer-items", { count: itemCount }) }}</span>
            </div>
          </div>
          <button
            v-if="filledItems.length"
            type="button"
            class="entrepot-review__list-btn"
            @click="reviewSheet = false"
          >
            {{ $t("entrepot-mobile-items-label") }} ({{ filledItems.length }})
          </button>
        </div>
        <div class="entrepot-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            height="48"
            class="entrepot-edit-sheet__submit"
            :loading="entrepot.isLoading"
            @click="onSubmit"
          >
            {{ $t("submit") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- More -->
    <v-bottom-sheet v-model="moreSheet">
      <v-card class="entrepot-edit-sheet">
        <div class="entrepot-edit-sheet__handle" />
        <div class="entrepot-edit-sheet__header" dir="rtl">
          <span class="entrepot-edit-sheet__spacer" />
          <div class="entrepot-edit-sheet__title">{{ $t("more") }}</div>
          <button
            type="button"
            class="entrepot-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="moreSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="entrepot-more-sheet__body" dir="rtl">
          <button
            v-if="!entrepot.formType"
            type="button"
            class="entrepot-more-sheet__item"
            @click="goToList"
          >
            <v-icon size="22" color="#5A6B85">mdi-format-list-numbered</v-icon>
            <span>{{ $t("list") }} {{ $t(entrepot.type) }}</span>
          </button>
          <button
            v-if="entrepot.documentPermmission"
            type="button"
            class="entrepot-more-sheet__item"
            @click="openDocuments"
          >
            <v-icon size="22" color="#5A6B85">mdi-paperclip</v-icon>
            <span>{{ $t("documents") }}</span>
          </button>
          <div v-if="entrepot.id" class="entrepot-more-sheet__excel">
            <export-excel
              :data="entrepot.excelItems"
              worksheet="abriron.com"
              name="data-ai.xls"
              type="xls"
              :title="$store.state.abrName"
              :total-prop="1"
              :page-prop="1"
              :excelIndex="true"
              @getAllRows="entrepot.getAllRows"
            >
              <button type="button" class="entrepot-more-sheet__item">
                <v-icon size="22" color="#00897B">mdi-file-excel-outline</v-icon>
                <span>{{ $t("get-excel") }}</span>
              </button>
            </export-excel>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="documentsSheet" eager>
      <v-card class="entrepot-edit-sheet entrepot-documents-sheet">
        <div class="entrepot-edit-sheet__handle" />
        <div class="entrepot-edit-sheet__header" dir="rtl">
          <span class="entrepot-edit-sheet__spacer" />
          <div class="entrepot-edit-sheet__title">
            {{ $t("attachments") }} {{ $t("documents") }}
          </div>
          <button
            type="button"
            class="entrepot-mobile-header__icon-btn"
            :aria-label="$t('back')"
            @click="closeDocumentsSheet"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div class="entrepot-edit-sheet__body entrepot-documents-sheet__body" dir="rtl">
          <div class="entrepot-documents-sheet__hint">
            {{ $t("entrepot-mobile-documents-hint") }}
          </div>
          <form-attachment-doc
            ref="mobileFilePicker"
            v-model="entrepot.attachments"
            type-prop="entrepot"
            :show-header="false"
            :val="entrepot.id && entrepot.attachments ? entrepot.attachments : null"
          />
        </div>
        <div class="entrepot-edit-sheet__footer">
          <v-btn
            color="primary"
            block
            depressed
            height="48"
            class="entrepot-edit-sheet__submit"
            @click="closeDocumentsSheet"
          >
            {{ $t("Save") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Entity / product picker (same UX as invoice customer/product layer) -->
    <v-dialog
      v-model="entityPickerOpen"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      content-class="entrepot-entity-picker-dialog"
    >
      <v-card class="entrepot-entity-picker">
        <header class="entrepot-entity-picker__header" dir="ltr">
          <button
            type="button"
            class="entrepot-entity-picker__icon-btn"
            :aria-label="$t('back')"
            @click="closeEntityPicker"
          >
            <v-icon size="26" color="#37474F">mdi-arrow-left</v-icon>
          </button>
          <h2 class="entrepot-entity-picker__title">{{ entityPickerTitle }}</h2>
          <div class="entrepot-entity-picker__actions">
            <button
              v-if="isProductPicker && canCreateProduct"
              type="button"
              class="entrepot-entity-picker__icon-btn"
              :aria-label="$t('product')"
              @click="openNewProductDialog"
            >
              <v-icon size="22" color="#607D8B">mdi-plus</v-icon>
            </button>
          </div>
        </header>

        <div class="entrepot-entity-picker__search" dir="rtl">
          <div class="entrepot-entity-picker__search-box">
            <v-icon size="22" color="#78909C" class="entrepot-entity-picker__search-icon">
              mdi-magnify
            </v-icon>
            <input
              v-model="entityPickerSearch"
              type="search"
              class="entrepot-entity-picker__search-input"
              :placeholder="$t('search')"
              autocomplete="off"
              @keydown.enter.prevent
            />
            <button
              v-if="entityPickerSearch"
              type="button"
              class="entrepot-entity-picker__search-clear"
              :aria-label="$t('clear')"
              @click="entityPickerSearch = ''"
            >
              <v-icon size="18" color="#78909C">mdi-close-circle</v-icon>
            </button>
          </div>
        </div>

        <div class="entrepot-entity-picker__divider">
          <v-progress-linear
            v-if="entityPickerLoading"
            indeterminate
            color="#78909C"
            height="2"
            class="entrepot-entity-picker__loader"
          />
        </div>

        <div
          ref="entityPickerBody"
          class="entrepot-entity-picker__body"
          dir="rtl"
          @scroll.passive="onEntityPickerScroll"
        >
          <button
            v-for="(item, index) in entityPickerItems"
            :key="item.id != null ? item.id : index"
            type="button"
            class="entrepot-entity-picker__item"
            :class="{
              'entrepot-entity-picker__item--active': isEntityPickerSelected(item),
            }"
            @click="selectEntityPickerItem(item)"
          >
            <span class="entrepot-entity-picker__index">{{ index + 1 }}</span>
            <span class="entrepot-entity-picker__item-main">
              <span class="entrepot-entity-picker__item-title">
                {{ entityPickerItemTitle(item) }}
              </span>
              <span
                v-if="isProductPicker && item.stock && item.stock.count != null"
                class="entrepot-entity-picker__item-sub"
              >
                {{ $t("stock") }}: {{ $numberFormat(item.stock.count, 3) }}
              </span>
              <span
                v-else-if="
                  entityPickerType === 'getInvoiceForEntrepot' && item.number
                "
                class="entrepot-entity-picker__item-sub"
              >
                {{ item.number }}
              </span>
            </span>
            <v-icon v-if="isEntityPickerSelected(item)" size="20" color="#607D8B">
              mdi-check
            </v-icon>
          </button>

          <div
            v-show="entityPickerHasMore"
            v-observe-visibility="{
              callback: onEntityPickerVisible,
              throttle: 200,
            }"
            class="entrepot-entity-picker__more"
          >
            <v-progress-circular
              v-if="entityPickerLoading"
              :size="20"
              :width="2"
              color="#78909C"
              indeterminate
            />
          </div>

          <div
            v-if="!entityPickerLoading && !entityPickerItems.length"
            class="entrepot-entity-picker__empty"
          >
            {{ $t("no-data") }}
          </div>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="newProductDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card class="entrepot-new-product-dialog">
        <v-col class="d-flex pt-2 pb-0">
          <v-btn icon @click="newProductDialog = false">
            <v-icon color="grey" large>mdi-close</v-icon>
          </v-btn>
          <p class="mx-auto font-weight-medium mt-2">
            {{ $t("product") }}
          </p>
        </v-col>
        <v-card-text class="pa-2">
          <product-form
            v-if="newProductDialog"
            v-model="newProduct"
            form-type="product"
            @addproduct="onCreatedProduct"
            @close="newProductDialog = false"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "EntrepotMobile",
  inject: ["entrepot"],
  components: {
    autocomplete: () =>
      import("../../../../components/general/patial/autocomplete"),
    Status: () => import("../../../../components/general/Status/status"),
    Tag: () => import("../../../../components/general/Tag/tag"),
    FormAttachmentDoc: () =>
      import("../../../../components/general/FormAttachmentDoc.vue"),
    ProductForm: () => import("../../../product/product"),
  },
  data() {
    return {
      moreSheet: false,
      documentsSheet: false,
      metaSheet: false,
      metaDateSheet: false,
      metaStatusTagSheet: false,
      metaContentSheet: false,
      expireDateSheet: false,
      editSheet: false,
      reviewSheet: false,
      editItem: null,
      rowUnitMenuOpen: false,
      rowSerialSheet: false,
      rowSerialInput: "",
      rowSerialSearch: "",
      rowSerialDuplicateHint: false,
      rowSerialLocal: [],
      rowSerialSelectedById: {},
      rowSerialSelectionState: {},
      rowSerialSearchTimer: null,
      productPickerMode: "product",
      rowNumpadOpen: false,
      rowNumpadField: null,
      rowNumpadItem: null,
      rowNumpadDraft: "0",
      rowNumpadPrevious: 0,
      rowNumpadLabel: "",
      rowNumpadReplaceNext: true,
      entityPickerOpen: false,
      entityPickerType: null,
      entityPickerTitle: "",
      entityPickerAssign: "header",
      entityPickerData: null,
      entityPickerSearch: "",
      entityPickerItems: [],
      entityPickerLoading: false,
      entityPickerPage: 1,
      entityPickerLastPage: 1,
      entityPickerTimer: null,
      entityPickerCancelSource: null,
      productPickerRowIndex: null,
      entityPickerReturnToMeta: false,
      documentsReturnToMeta: false,
      newProductDialog: false,
      newProduct: null,
    };
  },
  computed: {
    mobileShellStyle() {
      return { height: "100%", maxHeight: "100%" };
    },
    canCreateProduct() {
      const perms = this.$store.state.permissions || [];
      return perms.includes("Product") || perms.includes("product");
    },
    isProductPicker() {
      return (
        this.entityPickerType === "product" ||
        this.entityPickerType === "productService"
      );
    },
    entityPickerHasMore() {
      return (
        !!this.entityPickerItems.length &&
        this.entityPickerPage < this.entityPickerLastPage
      );
    },
    rowNumpadDisplay() {
      const raw = String(this.rowNumpadDraft == null ? "0" : this.rowNumpadDraft);
      return raw;
    },
    statusSubtitle() {
      const s = this.entrepot.status;
      if (!s) return this.$t("entrepot-mobile-select-status");
      return s.name || s.title || s.text || s.label || String(s.id || s);
    },
    relationsSubtitle() {
      const r = this.entrepot.relations;
      if (!r) return this.$t("entrepot-mobile-select-relation");
      return r.name || r.number || r.title || `#${r.id}` || "—";
    },
    tagSubtitle() {
      const t = this.entrepot.tag;
      if (!t) return this.$t("entrepot-mobile-select-tag");
      if (Array.isArray(t)) {
        const names = t
          .map((x) => (x && (x.name || x.title)) || null)
          .filter(Boolean);
        return names.length ? names.join("، ") : this.$t("entrepot-mobile-select-tag");
      }
      return t.name || t.title || this.$t("entrepot-mobile-select-tag");
    },
    contentSubtitle() {
      const c = this.entrepot.content;
      if (!c) return this.$t("entrepot-mobile-content-placeholder");
      const text = typeof c === "string" ? c.trim() : (c.name || c.text || "").trim();
      return text || this.$t("entrepot-mobile-content-placeholder");
    },
    rowSerialIsOutput() {
      return this.entrepot.serialFlow === "out";
    },
    rowSerialIsInput() {
      return this.entrepot.serialFlow !== "out";
    },
    rowSerialLoading() {
      return !!this.entrepot.serialsLoading;
    },
    rowSerialHasMore() {
      return !!this.entrepot.serialHasMore;
    },
    rowSerialAvailableCount() {
      return Array.isArray(this.rowSerialLocal) ? this.rowSerialLocal.length : 0;
    },
    rowSerialEffectiveCount() {
      if (this.rowSerialIsOutput) {
        return Object.keys(this.rowSerialSelectedById).length;
      }
      return this.rowSerialLocal.length;
    },
    rowSerialVisibleList() {
      if (this.rowSerialIsOutput) return this.rowSerialLocal;
      const q = (this.rowSerialSearch || "").toString().trim().toLowerCase();
      if (!q) return this.rowSerialLocal;
      return this.rowSerialLocal.filter((item) =>
        String(item.value || "")
          .toLowerCase()
          .includes(q)
      );
    },
    rowSerialOptionsTick() {
      const item = this.entrepot.serialItem;
      const opts = item && item.serialOptions;
      const len = Array.isArray(opts) ? opts.length : 0;
      const loading = this.entrepot.serialsLoading ? 1 : 0;
      const page = this.entrepot.serialPage || 0;
      return `${len}:${loading}:${page}:${item && item.id}`;
    },
    rowSerialAllVisibleSelected() {
      const list = this.rowSerialVisibleList;
      return (
        list.length > 0 &&
        list.every((item) => item && item.id != null && item.selected)
      );
    },
    rowSerialEmptyTitle() {
      if (this.rowSerialIsOutput) {
        return this.rowSerialSearch
          ? this.$t("invoice-mobile-serial-empty-search-title")
          : this.$t("invoice-mobile-serial-empty-warehouse-title");
      }
      return this.$t("invoice-mobile-serial-empty-input-title");
    },
    rowSerialEmptyHint() {
      if (this.rowSerialIsOutput) {
        return this.rowSerialSearch
          ? this.$t("invoice-mobile-serial-empty-search-hint")
          : this.$t("invoice-mobile-serial-empty-warehouse-hint");
      }
      return this.$t("invoice-mobile-serial-empty-input-hint");
    },
    rowSerialConfirmLabel() {
      if (this.rowSerialIsOutput) {
        if (!this.rowSerialEffectiveCount) {
          return this.$t("invoice-mobile-confirm-selection");
        }
        return this.$t("invoice-mobile-confirm-selection-count", {
          selected: this.rowSerialEffectiveCount,
          total: this.rowSerialAvailableCount,
        });
      }
      if (!this.rowSerialEffectiveCount) return this.$t("confirm");
      return this.$t("invoice-mobile-confirm-serials-count", {
        count: this.rowSerialEffectiveCount,
      });
    },
    statusTagSubtitle() {
      const statusText =
        this.statusSubtitle !== this.$t("entrepot-mobile-select-status")
          ? this.statusSubtitle
          : null;
      const tagText =
        this.tagSubtitle !== this.$t("entrepot-mobile-select-tag")
          ? this.tagSubtitle
          : null;
      const parts = [statusText, tagText].filter(Boolean);
      return parts.length
        ? parts.join(" • ")
        : this.$t("entrepot-mobile-status-tag-placeholder");
    },
    isTransferType() {
      const t = this.entrepot.type;
      return (
        t === "transferRemittance" ||
        t === "transferProductStock" ||
        t === "transferReceipt"
      );
    },
    showRelations() {
      const t = this.entrepot.type;
      return t === "receipt" || t === "remittance" || t === "tempReceipt";
    },
    showDetail() {
      return this.entrepot.type === "useRemittance";
    },
    showToProduct() {
      return this.entrepot.type === "transferProductStock";
    },
    showCountingFields() {
      return this.entrepot.type === "warehouseHandling";
    },
    showPriceField() {
      return this.entrepot.type === "warehouseHandling";
    },
    showExpireDate() {
      return this.entrepot.type !== "transferProductStock";
    },
    showCostActions() {
      return (
        !!this.entrepot.costManagament &&
        (this.entrepot.type === "receipt" || this.entrepot.type === "tempReceipt")
      );
    },
    productFieldTitle() {
      return this.showToProduct ? this.$t("fromProduct") : this.$t("product");
    },
    countLabel() {
      return this.entrepot.type === "warehouseHandling" ||
        this.$route.name === "warehouseHandlingEdit"
        ? this.$t("counting_final")
        : this.$t("count");
    },
    metaCardLabel() {
      if (this.isTransferType) return this.$t("entrepot-mobile-transfer-meta-label");
      if (this.entrepot.type === "remittance" || this.entrepot.type === "useRemittance") {
        return this.$t("entrepot-mobile-remittance-meta-label");
      }
      return this.$t("entrepot-mobile-receipt-meta-label");
    },
    primaryCardLabel() {
      if (this.isTransferType) return this.$t("recipientEntrepot");
      return this.$t("entrepot");
    },
    primaryCardPlaceholder() {
      if (this.isTransferType) return this.$t("entrepot-mobile-select-recipient");
      return this.$t("entrepot-mobile-select-entrepot");
    },
    primaryCardValue() {
      if (this.isTransferType) {
        return this.entityLabel(this.entrepot.recipientEntrepot);
      }
      return this.entityLabel(this.entrepot.entrepot);
    },
    metaCardText() {
      const parts = [
        this.entityLabel(this.entrepot.branch),
        this.entrepot.date || null,
        this.statusTagSubtitle !==
        this.$t("entrepot-mobile-status-tag-placeholder")
          ? this.statusTagSubtitle
          : null,
      ].filter(Boolean);
      return parts.length ? parts.join(" • ") : "";
    },
    filledItems() {
      return (this.entrepot.filteredItems || this.entrepot.items || []).filter(
        (item) => item && item.product
      );
    },
    itemCount() {
      return this.filledItems.length;
    },
    editItemIndex() {
      if (!this.editItem) return -1;
      return (this.entrepot.items || []).indexOf(this.editItem);
    },
  },
  watch: {
    editSheet(val) {
      if (!val) {
        this.editItem = null;
        this.rowUnitMenuOpen = false;
      }
    },
    "entrepot.deletDialog"(val) {
      if (val) return;
      if (this.editItem && this.editItemIndex === -1) {
        this.editSheet = false;
        this.editItem = null;
      }
    },
    "entrepot.render"(val) {
      if (val) this.syncFilePickerRef();
    },
    entityPickerSearch() {
      if (!this.entityPickerOpen || !this.entityPickerType) return;
      this.fetchEntityPickerItems(true);
    },
    entityPickerOpen(val, oldVal) {
      if (oldVal && !val && this.entityPickerReturnToMeta) {
        this.entityPickerReturnToMeta = false;
        this.$nextTick(() => {
          this.metaSheet = true;
        });
      }
    },
    documentsSheet(val) {
      if (val) this.syncFilePickerRef();
    },
    rowSerialSearch(val) {
      if (!this.rowSerialSheet || !this.rowSerialIsOutput) return;
      clearTimeout(this.rowSerialSearchTimer);
      this.rowSerialSearchTimer = setTimeout(() => {
        if (typeof this.entrepot.onSerialSearch === "function") {
          this.entrepot.onSerialSearch((val || "").toString().trim());
        }
      }, 400);
    },
    rowSerialOptionsTick() {
      if (!this.rowSerialSheet || !this.rowSerialIsOutput) return;
      this.syncRowSerialLocalFromEntrepot();
    },
    newProduct(val) {
      if (val) this.onCreatedProduct(val);
    },
  },
  mounted() {
    this.syncFilePickerRef();
  },
  beforeDestroy() {
    if (
      this.entrepot.$refs &&
      this.entrepot.$refs.refFilePicker === this.$refs.mobileFilePicker
    ) {
      this.$delete(this.entrepot.$refs, "refFilePicker");
    }
    if (this.entityPickerCancelSource) {
      this.entityPickerCancelSource.cancel("destroy");
    }
    clearTimeout(this.entityPickerTimer);
    clearTimeout(this.rowSerialSearchTimer);
  },
  methods: {
    entityLabel(entity) {
      if (!entity) return null;
      return entity.name || entity.fame || entity.title || entity.abrName || null;
    },
    syncFilePickerRef() {
      this.$nextTick(() => {
        if (this.$refs.mobileFilePicker) {
          this.entrepot.$refs.refFilePicker = this.$refs.mobileFilePicker;
        }
      });
    },
    closeAllSheets() {
      this.moreSheet = false;
      this.documentsSheet = false;
      this.documentsReturnToMeta = false;
      this.entityPickerReturnToMeta = false;
      this.metaSheet = false;
      this.metaDateSheet = false;
      this.metaStatusTagSheet = false;
      this.metaContentSheet = false;
      this.expireDateSheet = false;
      this.rowNumpadOpen = false;
      this.rowSerialSheet = false;
      this.editSheet = false;
      this.reviewSheet = false;
      this.editItem = null;
      this.closeEntityPicker();
    },
    openMetaSubSheet(sheetKey) {
      this.metaSheet = false;
      this.$nextTick(() => {
        this[sheetKey] = true;
      });
    },
    closeAllMetaSubSheets() {
      this.metaDateSheet = false;
      this.metaStatusTagSheet = false;
      this.metaContentSheet = false;
      this.documentsSheet = false;
    },
    backToMetaSheet() {
      this.documentsReturnToMeta = false;
      this.closeAllMetaSubSheets();
      this.$nextTick(() => {
        this.metaSheet = true;
      });
    },
    onMetaDateClose() {
      if (typeof this.entrepot.setDate === "function") {
        this.entrepot.setDate();
      }
    },
    openMetaDocuments() {
      this.metaSheet = false;
      this.documentsReturnToMeta = true;
      this.$nextTick(() => {
        this.documentsSheet = true;
      });
    },
    closeDocumentsSheet() {
      const returnToMeta = this.documentsReturnToMeta;
      this.documentsReturnToMeta = false;
      this.documentsSheet = false;
      if (returnToMeta) {
        this.$nextTick(() => {
          this.metaSheet = true;
        });
      }
    },
    openBranchPicker(assign) {
      this.metaSheet = false;
      this.entityPickerReturnToMeta = true;
      const data =
        assign === "recipientBranch"
          ? JSON.stringify({ withoutAccess: true })
          : null;
      const title =
        assign === "recipientBranch"
          ? this.$t("recipientBranch")
          : this.$t("branch");
      this.openEntityPicker("branch", title, assign, data);
    },
    openRelationsPicker() {
      this.metaSheet = false;
      this.entityPickerReturnToMeta = true;
      this.openEntityPicker(
        "getInvoiceForEntrepot",
        this.$t("relative-document"),
        "relations",
        JSON.stringify({
          refId: null,
          currency_id: null,
          checkCount: true,
          type: this.entrepot.type,
        })
      );
    },
    openDetailPicker() {
      this.metaSheet = false;
      this.entityPickerReturnToMeta = true;
      this.openEntityPicker("detail", this.$t("detail"), "detail");
    },
    entityPickerItemTitle(item) {
      if (!item) return "";
      return (
        item.name ||
        item.fame ||
        item.title ||
        item.number ||
        item.abrName ||
        (item.id != null ? `#${item.id}` : "")
      );
    },
    openPrimaryPicker() {
      this.entityPickerReturnToMeta = false;
      if (this.isTransferType) {
        this.openEntityPicker(
          "entrepot",
          this.$t("recipientEntrepot"),
          "recipient",
          JSON.stringify({ withoutAccess: true })
        );
      } else {
        this.openEntityPicker("entrepot", this.$t("entrepot"), "header");
      }
    },
    openEntityPicker(type, title, assign = "header", data = null) {
      this.entityPickerType = type;
      this.entityPickerTitle = title || "";
      this.entityPickerAssign = assign || "header";
      this.entityPickerData = data || null;
      this.entityPickerItems = [];
      this.entityPickerPage = 1;
      this.entityPickerLastPage = 1;
      this.entityPickerOpen = true;
      if (this.entityPickerSearch) {
        this.entityPickerSearch = "";
      } else {
        this.fetchEntityPickerItems(true);
      }
    },
    closeEntityPicker() {
      this.entityPickerOpen = false;
      this.productPickerRowIndex = null;
      this.entityPickerAssign = "header";
      this.entityPickerData = null;
      if (this.entityPickerCancelSource) {
        this.entityPickerCancelSource.cancel("picker closed");
        this.entityPickerCancelSource = null;
      }
      clearTimeout(this.entityPickerTimer);
    },
    currentEntityPickerValue() {
      if (this.entityPickerType === "entrepot") {
        if (this.entityPickerAssign === "recipient") {
          return this.entrepot.recipientEntrepot;
        }
        if (this.entityPickerAssign === "rowEntrepot" && this.editItem) {
          return this.editItem.article_entrepot || this.entrepot.entrepot;
        }
        return this.entrepot.entrepot;
      }
      if (this.entityPickerType === "branch") {
        if (this.entityPickerAssign === "recipientBranch") {
          return this.entrepot.recipientBranch;
        }
        return this.entrepot.branch;
      }
      if (this.entityPickerType === "detail") return this.entrepot.detail;
      if (this.entityPickerType === "getInvoiceForEntrepot") {
        return this.entrepot.relations;
      }
      if (this.isProductPicker && this.productPickerRowIndex != null) {
        const item = this.entrepot.items[this.productPickerRowIndex];
        return item && item.product;
      }
      return null;
    },
    isEntityPickerSelected(item) {
      const current = this.currentEntityPickerValue();
      if (!item || !current) return false;
      return current.id === item.id;
    },
    selectEntityPickerItem(item) {
      if (!item) return;
      if (this.entityPickerType === "entrepot") {
        if (this.entityPickerAssign === "recipient") {
          this.entrepot.recipientEntrepot = item;
        } else if (this.entityPickerAssign === "rowEntrepot" && this.editItem) {
          this.$set(this.editItem, "article_entrepot", item);
          this.entrepot.changeEntrepotAtRow(this.editItem, this.editItemIndex);
        } else {
          this.entrepot.entrepot = item;
        }
        this.closeEntityPicker();
        return;
      }
      if (this.entityPickerType === "branch") {
        if (this.entityPickerAssign === "recipientBranch") {
          this.entrepot.recipientBranch = item;
        } else {
          this.entrepot.branch = item;
        }
        this.closeEntityPicker();
        return;
      }
      if (this.entityPickerType === "detail") {
        this.entrepot.detail = item;
        this.closeEntityPicker();
        return;
      }
      if (this.entityPickerType === "getInvoiceForEntrepot") {
        this.entrepot.relations = item;
        this.closeEntityPicker();
        return;
      }
      if (this.isProductPicker) {
        this.applySelectedProduct(item);
      }
    },
    openProductPicker(rowIndex) {
      this.entityPickerReturnToMeta = false;
      this.productPickerMode = "product";
      this.productPickerRowIndex =
        rowIndex === undefined || rowIndex === null ? null : rowIndex;
      this.openEntityPicker("product", this.$t("product"), "product");
    },
    openToProductPicker() {
      if (!this.editItem) return;
      this.entityPickerReturnToMeta = false;
      this.productPickerMode = "toProduct";
      this.productPickerRowIndex = this.editItemIndex;
      this.openEntityPicker("product", this.$t("toProduct"), "product");
    },
    changeRowProductFromDetail() {
      if (!this.editItem) return;
      this.productPickerMode = "product";
      this.openProductPicker(this.editItemIndex);
    },
    openRowEntrepotPicker() {
      this.entityPickerReturnToMeta = false;
      this.openEntityPicker("entrepot", this.$t("entrepot"), "rowEntrepot");
    },
    selectRowUnit(unit) {
      if (!this.editItem || !unit) return;
      this.$set(this.editItem, "unit", unit);
      this.entrepot.changeUnit(this.editItem);
      this.rowUnitMenuOpen = false;
    },
    rowSerialLabel(item) {
      const serials = (item && item.serials) || [];
      if (!serials.length) {
        return this.$t("invoice-mobile-serial-row-placeholder");
      }
      if (serials.length === 1) {
        return (
          serials[0].value ||
          serials[0].serial ||
          serials[0].name ||
          serials[0] ||
          this.$t("invoice-mobile-one-serial")
        );
      }
      return this.$t("invoice-mobile-serial-count", { count: serials.length });
    },
    openRowSerialDialog() {
      if (!this.editItem) return;
      if (typeof this.entrepot.openSerialDialog === "function") {
        this.entrepot.openSerialDialog(this.editItem);
      }
      if (this.entrepot.serialDialog) {
        this.entrepot.serialDialog = false;
      }
      this.resetRowSerialDraft();
      this.seedRowSerialSelectionFromItem(this.editItem);
      this.syncRowSerialLocalFromEntrepot();
      this.rowSerialSheet = true;
      this.$nextTick(() => this.focusRowSerialInput());
    },
    closeRowSerialSheet() {
      this.rowSerialSheet = false;
      this.rowSerialInput = "";
      this.rowSerialSearch = "";
      this.rowSerialDuplicateHint = false;
      clearTimeout(this.rowSerialSearchTimer);
    },
    resetRowSerialDraft() {
      this.rowSerialInput = "";
      this.rowSerialSearch = "";
      this.rowSerialDuplicateHint = false;
      this.rowSerialLocal = [];
      this.rowSerialSelectedById = {};
      this.rowSerialSelectionState = {};
      clearTimeout(this.rowSerialSearchTimer);
    },
    formatRowSerialDate(date) {
      if (!date) return "";
      const str = String(date);
      if (/^\d{4}\/\d{2}\/\d{2}$/.test(str)) return str;
      if (!this.$moment) return str;
      const m = this.$moment(str);
      return m.isValid() ? m.format("jYYYY/jMM/jDD") : str;
    },
    seedRowSerialSelectionFromItem(item) {
      if (!this.rowSerialIsOutput || !item) return;
      const serials = Array.isArray(item.serials) ? item.serials : [];
      serials.forEach((serial) => {
        const id = serial && (serial.id || serial.serial_id);
        if (id == null) return;
        const row = {
          id,
          uid: serial.uid || id,
          value: String(serial.value || serial.serial || "").trim(),
          created_at: this.formatRowSerialDate(serial.created_at),
          selected: true,
          product_id: serial.product_id || null,
        };
        this.$set(this.rowSerialSelectedById, id, row);
        this.$set(this.rowSerialSelectionState, id, true);
      });
    },
    rememberRowSerialSelection(item) {
      if (!this.rowSerialIsOutput || !item || item.id == null) return;
      this.$set(this.rowSerialSelectionState, item.id, !!item.selected);
      if (item.selected) {
        this.$set(this.rowSerialSelectedById, item.id, {
          ...item,
          selected: true,
        });
      } else {
        this.$delete(this.rowSerialSelectedById, item.id);
      }
    },
    syncRowSerialLocalFromEntrepot() {
      const item = this.entrepot.serialItem || this.editItem;
      if (!item) {
        this.rowSerialLocal = [];
        return;
      }
      let source = [];
      if (this.rowSerialIsOutput) {
        source = Array.isArray(item.serialOptions)
          ? item.serialOptions
          : Array.isArray(this.entrepot.serialDialogList)
          ? this.entrepot.serialDialogList
          : [];
      } else {
        source = Array.isArray(item.serials) ? item.serials : this.rowSerialLocal;
        if (this.rowSerialLocal.length && this.rowSerialSheet) {
          return;
        }
      }
      this.rowSerialLocal.forEach((row) => this.rememberRowSerialSelection(row));
      this.rowSerialLocal = (source || [])
        .map((serial, index) => {
          const id = serial.id || null;
          const remembered =
            this.rowSerialIsOutput &&
            id != null &&
            this.rowSerialSelectionState[id] !== undefined;
          return {
            id,
            uid:
              serial.uid ||
              serial.id ||
              `${serial.value || serial.serial}-${index}`,
            value: String(serial.value || serial.serial || "").trim(),
            created_at: this.formatRowSerialDate(serial.created_at),
            selected: remembered
              ? !!this.rowSerialSelectionState[id]
              : serial.selected !== undefined
              ? !!serial.selected
              : !this.rowSerialIsOutput,
            product_id: serial.product_id || null,
          };
        })
        .filter((row) => row.value);
      this.rowSerialLocal.forEach((row) => this.rememberRowSerialSelection(row));
    },
    focusRowSerialInput() {
      if (!this.rowSerialIsInput) return;
      const el = this.$refs.rowSerialInput;
      if (el && typeof el.focus === "function") el.focus();
    },
    addRowSerial() {
      if (!this.rowSerialIsInput) return;
      const value = (this.rowSerialInput || "").toString().trim();
      if (!value) return;
      const exists = this.rowSerialLocal.some(
        (row) => String(row.value).toLowerCase() === value.toLowerCase()
      );
      if (exists) {
        this.rowSerialDuplicateHint = true;
        this.rowSerialInput = "";
        this.$nextTick(() => this.focusRowSerialInput());
        return;
      }
      this.rowSerialDuplicateHint = false;
      this.rowSerialLocal.unshift({
        uid: `${Date.now()}-${value}`,
        id: null,
        value,
        created_at: this.$moment ? this.$moment().format("jYYYY/jMM/jDD") : "",
        selected: true,
        product_id:
          this.editItem && this.editItem.product && this.editItem.product.id
            ? this.editItem.product.id
            : null,
      });
      this.rowSerialInput = "";
      this.$nextTick(() => this.focusRowSerialInput());
    },
    removeRowSerial(item) {
      if (!this.rowSerialIsInput || !item) return;
      this.rowSerialLocal = this.rowSerialLocal.filter((row) => row.uid !== item.uid);
      this.$nextTick(() => this.focusRowSerialInput());
    },
    onRowSerialItemClick(item) {
      if (!this.rowSerialIsOutput) return;
      this.toggleRowSerialSelect(item);
    },
    toggleRowSerialSelect(item) {
      if (!this.rowSerialIsOutput || !item) return;
      const listRow =
        item.id != null
          ? this.rowSerialLocal.find((row) => row.id === item.id)
          : null;
      const target = listRow || item;
      target.selected = !target.selected;
      this.rememberRowSerialSelection(target);
      if (listRow && item !== listRow) {
        item.selected = listRow.selected;
      }
    },
    toggleSelectAllRowSerials() {
      if (!this.rowSerialIsOutput) return;
      const selectAll = !this.rowSerialAllVisibleSelected;
      this.rowSerialVisibleList.forEach((item) => {
        if (!item || item.id == null) return;
        item.selected = selectAll;
        this.rememberRowSerialSelection(item);
      });
    },
    clearRowSerialSelection() {
      if (!this.rowSerialIsOutput) return;
      this.rowSerialLocal.forEach((item) => {
        if (!item) return;
        item.selected = false;
        this.rememberRowSerialSelection(item);
      });
      this.rowSerialSelectedById = {};
      this.rowSerialSelectionState = {};
    },
    loadMoreRowSerials() {
      if (typeof this.entrepot.loadMoreSerials === "function") {
        this.entrepot.loadMoreSerials();
      }
    },
    confirmRowSerialSheet() {
      this.rowSerialLocal.forEach((row) => this.rememberRowSerialSelection(row));
      const productId =
        this.editItem && this.editItem.product && this.editItem.product.id
          ? this.editItem.product.id
          : null;
      const rows = this.rowSerialIsOutput
        ? Object.keys(this.rowSerialSelectedById).map(
            (id) => this.rowSerialSelectedById[id]
          )
        : this.rowSerialLocal;
      const payload = rows.map((row) => ({
        id: row.id || null,
        product_id: productId,
        value: String(row.value),
        created_at: row.created_at,
        uid: row.uid,
        selected: true,
      }));
      if (typeof this.entrepot.setSerials === "function") {
        this.entrepot.setSerials(payload);
      } else if (this.editItem) {
        this.$set(this.editItem, "serials", payload);
      }
      this.closeRowSerialSheet();
    },
    openNewProductDialog() {
      this.newProduct = null;
      this.newProductDialog = true;
    },
    onCreatedProduct(product) {
      if (!product) return;
      this.newProductDialog = false;
      this.newProduct = null;
      this.applySelectedProduct(product, { fromCreate: true });
    },
    normalizeProductForRow(product) {
      if (!product) return product;
      const normalized = Object.assign({}, product);
      if (!normalized.id && normalized.fin_id) {
        normalized.id = normalized.fin_id;
      }
      if (!normalized.type) normalized.type = "product";
      if (!normalized.unit && normalized.unit_id) {
        normalized.unit = {
          id: normalized.unit_id,
          name: normalized.unit_name || normalized.unit_fame || this.$t("unit"),
          fame: normalized.unit_fame || normalized.unit_name || this.$t("unit"),
        };
      }
      if (normalized.unit && normalized.unit.name && !normalized.unit.fame) {
        normalized.unit.fame = normalized.unit.name;
      }
      if (!normalized.stock) normalized.stock = { count: 0 };
      return normalized;
    },
    applySelectedProduct(product, options = {}) {
      if (!product) return;
      product = this.normalizeProductForRow(product);
      const rowIndex = this.productPickerRowIndex;
      if (rowIndex != null) {
        const item = this.entrepot.items[rowIndex];
        if (item) {
          if (this.productPickerMode === "toProduct") {
            this.$set(item, "to_product", product);
          } else {
            this.$set(item, "product", product);
            this.entrepot.checkProduct(item, rowIndex);
            this.entrepot.unitItems(item);
            if (!item.unit && product.unit) this.$set(item, "unit", product.unit);
            if (!item.count) item.count = 1;
            if (typeof this.entrepot.syncRowSerialFlag === "function") {
              this.entrepot.syncRowSerialFlag(item);
            }
          }
        }
      } else {
        this.addProductAsRow(product);
      }
      this.productPickerMode = "product";
      this.productPickerRowIndex = null;
      this.closeEntityPicker();
    },
    addProductAsRow(product) {
      if (!product) return;
      const items = this.entrepot.items || [];
      if (items.length > 0 && !items[0].product) {
        items.splice(0, 1);
      }
      const existingIdx = items.findIndex(
        (row) => row && row.product && row.product.id === product.id
      );
      if (existingIdx !== -1) {
        const row = items[existingIdx];
        row.count = (parseFloat(row.count) || 0) + 1;
        this.entrepot.checkCount(row);
        return;
      }
      this.entrepot.addRow();
      const last = (this.entrepot.items || [])[this.entrepot.items.length - 1];
      if (!last) return;
      this.$set(last, "product", product);
      this.entrepot.checkProduct(last, this.entrepot.items.length - 1);
      this.entrepot.unitItems(last);
      if (!last.unit && product.unit) this.$set(last, "unit", product.unit);
      if (!last.count) last.count = 1;
    },
    onEntityPickerScroll(e) {
      const el = e && e.target;
      if (!el || this.entityPickerLoading || !this.entityPickerHasMore) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
        this.loadMoreEntityPicker();
      }
    },
    onEntityPickerVisible(isVisible) {
      if (isVisible) this.loadMoreEntityPicker();
    },
    loadMoreEntityPicker() {
      if (!this.entityPickerOpen || this.entityPickerLoading) return;
      if (this.entityPickerPage >= this.entityPickerLastPage) return;
      this.entityPickerPage += 1;
      this.fetchEntityPickerItems(false);
    },
    maybeFillEntityPicker() {
      this.$nextTick(() => {
        if (!this.entityPickerOpen || this.entityPickerLoading) return;
        if (this.entityPickerPage >= this.entityPickerLastPage) return;
        const body = this.$refs.entityPickerBody;
        if (!body) return;
        if (body.scrollHeight <= body.clientHeight + 40) {
          this.loadMoreEntityPicker();
        }
      });
    },
    fetchEntityPickerItems(reset) {
      if (!this.entityPickerType) return;
      if (reset) {
        this.entityPickerPage = 1;
        this.entityPickerItems = [];
        this.entityPickerLastPage = 1;
      }
      if (this.entityPickerCancelSource) {
        this.entityPickerCancelSource.cancel("Canceled due to new request");
      }
      this.entityPickerCancelSource = this.$axios.CancelToken.source();
      this.entityPickerLoading = true;
      clearTimeout(this.entityPickerTimer);
      const page = this.entityPickerPage;
      const type = this.entityPickerType;
      const search = (this.entityPickerSearch || "").trim();
      this.entityPickerTimer = setTimeout(() => {
        const params = {
          select: type,
          search,
          page,
        };
        if (this.entityPickerData) {
          params.data = this.entityPickerData;
        }
        if (type === "product" || type === "productService") {
          const wh = this.entrepot.entrepot;
          params.data = JSON.stringify({
            entrepot: wh ? wh.id : null,
            getCosts: this.entrepot.type === "warehouseHandling",
          });
          params.formType = this.entrepot.type;
        }
        this.$axios
          .get(this.$endpoint + "getdetail", {
            params,
            cancelToken: this.entityPickerCancelSource.token,
          })
          .then((res) => {
            const payload = res.data || {};
            const rows = Array.isArray(payload.data)
              ? Array.from(payload.data)
              : Array.isArray(payload)
              ? Array.from(payload)
              : [];
            let lastPage = Number(
              payload.last_page ||
                (payload.meta && payload.meta.last_page) ||
                (rows.length && page === 1 && rows.length < 10
                  ? 1
                  : page + (rows.length ? 1 : 0))
            );
            this.entityPickerLastPage =
              Number.isFinite(lastPage) && lastPage > 0 ? lastPage : 1;
            if (
              !payload.last_page &&
              !(payload.meta && payload.meta.last_page) &&
              rows.length >= 10
            ) {
              this.entityPickerLastPage = Math.max(
                this.entityPickerLastPage,
                page + 1
              );
            }
            if (reset || page === 1) {
              this.entityPickerItems = rows;
            } else {
              const map = new Map();
              for (const it of this.entityPickerItems) {
                map.set(it && it.id != null ? it.id : JSON.stringify(it), it);
              }
              for (const it of rows) {
                const key = it && it.id != null ? it.id : JSON.stringify(it);
                if (!map.has(key)) map.set(key, it);
              }
              this.entityPickerItems = Array.from(map.values());
            }
            if (!rows.length && page > 1) {
              this.entityPickerLastPage = page - 1;
              this.entityPickerPage = page - 1;
            }
          })
          .catch((err) => {
            if (
              !this.$axios.isCancel(err) &&
              typeof this.entrepot.serverErrorHandeler === "function"
            ) {
              this.entrepot.serverErrorHandeler(err);
            }
          })
          .finally(() => {
            this.entityPickerLoading = false;
            this.maybeFillEntityPicker();
          });
      }, 50);
    },
    productImageUrl(item) {
      const product = item && item.product;
      if (!product) return "";
      return (
        product.avatar ||
        product.image ||
        product.image_url ||
        (product.meta && product.meta.avatar) ||
        ""
      );
    },
    rowUnitLabel(item) {
      if (!item || !item.unit) return this.$t("unit");
      return item.unit.fame || item.unit.name || this.$t("unit");
    },
    costLabel(item) {
      const n = item && item.cost ? item.cost.length : 0;
      return n ? this.$t("entrepot-mobile-costs-count", { count: n }) : this.$t("costs");
    },
    goBack() {
      this.closeAllSheets();
      this.entrepot.$router.go(-1);
    },
    goToList() {
      this.closeAllSheets();
      if (typeof this.entrepot.goTo === "function") {
        this.entrepot.goTo(this.entrepot.type + "List");
      }
    },
    onRefresh() {
      if (this.entrepot.id && typeof this.entrepot.show === "function") {
        this.entrepot.render = false;
        this.entrepot.show();
      }
    },
    openDocuments() {
      this.moreSheet = false;
      this.documentsReturnToMeta = false;
      this.$nextTick(() => {
        this.documentsSheet = true;
      });
    },
    openReviewSheet() {
      this.reviewSheet = true;
    },
    onAddRow() {
      if (!this.entrepot.render || this.entrepot.isLoading) return;
      if (!this.entrepot.entrepot) {
        this.openPrimaryPicker();
        this.entrepot.snakbarText = this.$t("please fill the form below");
        this.entrepot.snakbarColor = "pink";
        this.entrepot.snakbar = true;
        return;
      }
      this.openProductPicker();
    },
    openRowDetail(item) {
      this.editItem = item;
      this.editSheet = true;
    },
    onDeleteRow(item) {
      if (this.editItem === item) {
        this.editSheet = false;
        this.editItem = null;
      }
      this.entrepot.openDeleteDialog(item);
    },
    bumpCount(item, delta) {
      if (!item) return;
      const next = (parseFloat(item.count) || 0) + delta;
      item.count = next < 0 ? 0 : next;
      if (item.count <= 0) {
        this.onDeleteRow(item);
        return;
      }
      if (typeof this.entrepot.checkCount === "function") {
        this.entrepot.checkCount(item);
      }
    },
    onEditProductInput() {
      if (!this.editItem) return;
      const key = this.editItemIndex;
      this.entrepot.checkProduct(this.editItem, key > -1 ? key : 0);
    },
    unitItems(item) {
      return this.entrepot.unitItems(item);
    },
    isAltUnit(item) {
      return !!(
        item &&
        item.product &&
        item.product.units &&
        item.unit &&
        item.product.unit &&
        item.unit.id !== item.product.unit.id
      );
    },
    openRowNumpad(field, item) {
      if (!item || !field) return;
      let value = 0;
      let label = this.$t("count");
      if (field === "count") {
        value = item.count;
        label = this.countLabel;
      } else if (field === "countUnit") {
        value = item.countUnit;
        label = this.$t("count");
      } else if (field === "counting_one") {
        value = item.counting_one;
        label = this.$t("counting_one");
      } else if (field === "counting_two") {
        value = item.counting_two;
        label = this.$t("counting_two");
      } else if (field === "price") {
        value = item.price;
        label = this.$t("price");
      } else {
        return;
      }
      const initial = value == null || value === "" ? 0 : value;
      this.rowNumpadField = field;
      this.rowNumpadItem = item;
      this.rowNumpadDraft = String(initial);
      this.rowNumpadPrevious = initial;
      this.rowNumpadLabel = label;
      this.rowNumpadReplaceNext = true;
      this.rowNumpadOpen = true;
    },
    parseNumpadNumber(data) {
      if (data === "" || data == null || data === ".") return 0;
      const num = parseFloat(data);
      return Number.isFinite(num) ? num : 0;
    },
    applyRowNumpadValue(data) {
      const item = this.rowNumpadItem;
      const field = this.rowNumpadField;
      if (!item || !field) return;
      const value = this.parseNumpadNumber(data);
      if (field === "count") {
        this.$set(item, "count", value);
        this.entrepot.checkCount(item);
        return;
      }
      if (field === "countUnit") {
        this.$set(item, "countUnit", value);
        this.entrepot.convertUnit(item);
        return;
      }
      if (field === "counting_one") {
        this.$set(item, "counting_one", value);
        return;
      }
      if (field === "counting_two") {
        this.$set(item, "counting_two", value);
        return;
      }
      if (field === "price") {
        this.$set(item, "price", value);
      }
    },
    rowNumpadDigit(n) {
      const digit = String(n);
      if (this.rowNumpadReplaceNext) {
        this.rowNumpadDraft = digit;
        this.rowNumpadReplaceNext = false;
        return;
      }
      if (this.rowNumpadDraft === "0") this.rowNumpadDraft = digit;
      else this.rowNumpadDraft = String(this.rowNumpadDraft) + digit;
    },
    rowNumpadAppend(chunk) {
      if (this.rowNumpadReplaceNext) {
        this.rowNumpadDraft = String(parseInt(chunk, 10) || 0);
        this.rowNumpadReplaceNext = false;
        return;
      }
      this.rowNumpadDraft = String(this.rowNumpadDraft) + String(chunk);
    },
    rowNumpadDot() {
      if (this.rowNumpadReplaceNext) {
        this.rowNumpadDraft = "0.";
        this.rowNumpadReplaceNext = false;
        return;
      }
      if (String(this.rowNumpadDraft).indexOf(".") === -1) {
        this.rowNumpadDraft = String(this.rowNumpadDraft) + ".";
      }
    },
    rowNumpadBack() {
      if (this.rowNumpadReplaceNext) {
        this.rowNumpadDraft = "0";
        return;
      }
      const next = String(this.rowNumpadDraft).slice(0, -1);
      this.rowNumpadDraft = next === "" || next === "-" ? "0" : next;
      if (this.rowNumpadDraft === "0") this.rowNumpadReplaceNext = true;
    },
    rowNumpadClear() {
      this.rowNumpadDraft = "0";
      this.rowNumpadReplaceNext = true;
    },
    rowNumpadStep(delta) {
      const value = this.parseNumpadNumber(this.rowNumpadDraft) + delta;
      this.rowNumpadDraft = String(value < 0 ? 0 : value);
      this.rowNumpadReplaceNext = true;
    },
    confirmRowNumpad() {
      this.applyRowNumpadValue(this.rowNumpadDraft);
      this.rowNumpadOpen = false;
      this.rowNumpadField = null;
      this.rowNumpadItem = null;
    },
    cancelRowNumpad() {
      this.rowNumpadOpen = false;
      this.rowNumpadField = null;
      this.rowNumpadItem = null;
      this.rowNumpadDraft = String(this.rowNumpadPrevious || 0);
    },
    validateBeforeSubmit() {
      if (!this.entrepot.entrepot || !this.entrepot.date) {
        if (!this.entrepot.entrepot) this.openPrimaryPicker();
        else this.metaSheet = true;
        this.entrepot.snakbarText = this.$t("please fill the form below");
        this.entrepot.snakbarColor = "pink";
        this.entrepot.snakbar = true;
        return false;
      }
      if (this.isTransferType && !this.entrepot.recipientEntrepot) {
        this.openPrimaryPicker();
        this.entrepot.snakbarText = this.$t("please fill the form below");
        this.entrepot.snakbarColor = "pink";
        this.entrepot.snakbar = true;
        return false;
      }
      if (this.showDetail && !this.entrepot.detail) {
        this.metaSheet = true;
        this.entrepot.snakbarText = this.$t("please fill the form below");
        this.entrepot.snakbarColor = "pink";
        this.entrepot.snakbar = true;
        return false;
      }
      return true;
    },
    onSubmit() {
      if (!this.entrepot.render || this.entrepot.isLoading) return;
      if (!this.validateBeforeSubmit()) {
        this.reviewSheet = false;
        return;
      }
      this.closeAllSheets();
      this.syncFilePickerRef();
      this.$nextTick(() => {
        if (this.entrepot.id) this.entrepot.update();
        else this.entrepot.create();
      });
    },
  },
};
</script>

<style scoped>
.entrepot-mobile {
  --em-radius: 8px;
  --em-blue: #1565c0;
  --em-blue-soft: #e3f2fd;
  --em-border: #cfd8dc;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  overscroll-behavior: none;
  background: #fff;
}

.entrepot-mobile-header {
  flex: 0 0 auto;
  direction: ltr;
  display: grid;
  grid-template-columns: 40px 1fr auto;
  align-items: center;
  gap: 4px;
  min-height: 52px;
  padding: 6px 10px;
  background: #fff;
  border-bottom: 1px solid #eceff1;
}

.entrepot-mobile-header__title {
  margin: 0;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: #263238;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.entrepot-mobile-header__actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

.entrepot-mobile-header__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.entrepot-mobile-header__icon-btn:active {
  background: #f5f5f5;
}

.entrepot-mobile-header__icon-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.entrepot-mobile__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 10px 10px 12px;
  background: #fff;
}

.entrepot-mobile-init {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

.entrepot-summary-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
}

.entrepot-summary-card {
  min-width: 0;
  min-height: 48px;
  padding: 8px 10px;
  border: 1px solid var(--em-border);
  border-radius: var(--em-radius);
  background: #fff;
  text-align: right;
  cursor: pointer;
}

.entrepot-summary-card--primary {
  flex: 1.35 1 0;
  display: flex;
  align-items: stretch;
  gap: 6px;
  padding: 4px 4px 4px 8px;
  cursor: default;
}

.entrepot-summary-card--meta {
  flex: 1.65 1 0;
}

.entrepot-summary-card__main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 1px;
  padding: 4px 4px 4px 0;
  border: 0;
  background: transparent;
  text-align: right;
  cursor: pointer;
}

.entrepot-summary-card__side {
  flex: 0 0 40px;
  width: 40px;
  min-height: 40px;
  align-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.entrepot-summary-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  margin-bottom: 2px;
}

.entrepot-summary-card__label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #78909c;
  line-height: 1.3;
}

.entrepot-summary-card__value {
  display: block;
  max-width: 100%;
  font-size: 12px;
  font-weight: 600;
  color: #37474f;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.entrepot-summary-card__value.is-placeholder {
  color: #90a4ae;
}

.entrepot-product-add-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
  width: 100%;
  margin-top: 10px;
}

.entrepot-summary-add-btn {
  flex: 0 0 48px;
  width: 48px;
  min-height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  background: var(--em-blue-soft);
  cursor: pointer;
}

.entrepot-summary-add-btn:active {
  background: #d0e7fc;
}

.entrepot-add-row-btn {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 14px;
  border: 1px dashed #b0bec5;
  border-radius: 8px;
  background: #f3f5f7;
  cursor: pointer;
}

.entrepot-add-row-btn__text {
  font-size: 14px;
  font-weight: 700;
  color: #546e7a;
}

.entrepot-mobile-empty {
  padding: 28px 12px;
  text-align: center;
  color: #90a4ae;
  font-size: 13px;
}

.entrepot-product-rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
  padding-bottom: 8px;
}

.entrepot-product-card {
  border: 1px solid #e8eef3;
  border-radius: 10px;
  background: #fff;
  padding: 10px;
}

.entrepot-product-card__top {
  display: flex;
  align-items: stretch;
  gap: 12px;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: right;
  cursor: pointer;
}

.entrepot-product-card__media {
  flex: 0 0 56px;
  width: 56px;
  height: 56px;
  border-radius: 8px;
  background: #f0f2f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.entrepot-product-card__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.entrepot-product-card__info {
  flex: 1 1 auto;
  min-width: 0;
}

.entrepot-product-card__title {
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 700;
  color: #263238;
  line-height: 1.35;
}

.entrepot-product-card__line {
  font-size: 12px;
  color: #78909c;
  line-height: 1.5;
}

.entrepot-product-card__line span {
  color: #37474f;
  font-weight: 600;
}

.entrepot-product-card__line--accent span {
  color: var(--em-blue);
}

.entrepot-product-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #f0f2f5;
}

.entrepot-product-card__tools {
  display: flex;
  align-items: center;
  gap: 2px;
}

.entrepot-product-card__tool {
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.entrepot-product-card__count {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px;
  border: 1px solid #e0e4e8;
  border-radius: 10px;
  background: #f7f9fb;
}

.entrepot-product-card__count-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.entrepot-product-card__count-value {
  min-width: 44px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  color: #37474f;
  font-size: 13px;
  font-weight: 700;
  text-align: center;
  cursor: pointer;
}

.entrepot-product-card__count-value small {
  display: block;
  font-size: 10px;
  font-weight: 600;
  color: #90a4ae;
}

.entrepot-mobile-footer {
  flex: 0 0 auto;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #eceff1;
  background: #fff;
}

.entrepot-mobile-footer__summary {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  font-weight: 500;
  color: #37474f;
  white-space: nowrap;
}

.entrepot-mobile-footer__btn {
  flex: 0 0 auto;
  min-height: 36px;
  padding: 7px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
}

.entrepot-mobile-footer__btn--outline {
  border: 1px solid var(--em-blue);
  background: #fff;
  color: var(--em-blue);
}

.entrepot-mobile-footer__btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.entrepot-mobile-footer__more {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.entrepot-edit-sheet {
  border-radius: 16px 16px 0 0 !important;
  overflow: hidden;
}

.entrepot-edit-sheet__handle {
  width: 36px;
  height: 4px;
  margin: 10px auto 0;
  border-radius: 6px;
  background: #cfd8dc;
}

.entrepot-edit-sheet__header {
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  align-items: center;
  min-height: 48px;
  padding: 4px 8px;
  border-bottom: 1px solid #eceff1;
}

.entrepot-edit-sheet__title {
  margin: 0;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
}

.entrepot-edit-sheet__title--solo {
  padding: 10px 16px 4px;
}

.entrepot-edit-sheet__spacer {
  width: 36px;
  height: 36px;
}

.entrepot-edit-sheet__body {
  max-height: min(70vh, 560px);
  overflow-y: auto;
  padding: 14px 14px 8px;
}

.entrepot-meta-sheet__body {
  padding-top: 4px;
  padding-bottom: 4px;
}

.entrepot-meta-sheet__nav-list {
  flex: 0 0 auto;
  margin: 0 -14px;
}

.entrepot-meta-sheet__item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 12px;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: #fff;
  text-align: right;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.entrepot-meta-sheet__item:active {
  background: #f7f9fc;
}

.entrepot-meta-sheet__nav-list .entrepot-meta-sheet__item:last-child {
  border-bottom: 0;
}

.entrepot-meta-sheet__icon {
  flex: 0 0 auto;
}

.entrepot-meta-sheet__texts {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 1 auto;
  min-width: 0;
  text-align: right;
}

.entrepot-meta-sheet__item-title {
  display: block;
  width: 100%;
  font-size: 14px;
  font-weight: 700;
  color: #212121;
  line-height: 1.35;
  text-align: right;
}

.entrepot-meta-sheet__item-sub {
  display: block;
  width: 100%;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 400;
  color: #90a4ae;
  line-height: 1.35;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.entrepot-mobile-date {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.entrepot-mobile-date__selected {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid #e0e6ed;
  border-radius: 12px;
  background: #f7f9fc;
}

.entrepot-mobile-date__label {
  width: 100%;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}

.entrepot-mobile-date__value {
  font-size: 18px;
  font-weight: 800;
  color: #263238;
  letter-spacing: 0.02em;
}

.entrepot-mobile-date__picker {
  width: 100%;
}

.entrepot-mobile-date__picker ::v-deep .vpd-main,
.entrepot-mobile-date__picker ::v-deep .vpd-wrapper,
.entrepot-mobile-date__picker ::v-deep .vpd-container,
.entrepot-mobile-date__picker ::v-deep .vpd-content {
  width: 100% !important;
  max-width: 100% !important;
  box-shadow: none !important;
  border: 1px solid #e0e6ed !important;
  border-radius: 12px !important;
  background: #fff !important;
  overflow: hidden;
}

.entrepot-mobile-date__picker ::v-deep .vpd-header,
.entrepot-mobile-date__picker ::v-deep .vpd-addon-list {
  background: #c5d0e0 !important;
  color: #37474f !important;
}

.entrepot-mobile-date__picker ::v-deep .vpd-header *,
.entrepot-mobile-date__picker ::v-deep .vpd-addon-list * {
  color: #37474f !important;
}

.entrepot-mobile-date__picker ::v-deep .vpd-day.selected span,
.entrepot-mobile-date__picker ::v-deep .vpd-day.selected,
.entrepot-mobile-date__picker ::v-deep .selected {
  background: #c5d0e0 !important;
  color: #37474f !important;
  box-shadow: none !important;
}

.entrepot-mobile-date__picker ::v-deep .vpd-input-group,
.entrepot-mobile-date__picker ::v-deep input {
  display: none !important;
}

.entrepot-row-numpad {
  padding: 6px 12px calc(12px + env(safe-area-inset-bottom, 0px));
  border-radius: 16px 16px 0 0 !important;
  background: #f3f5f7 !important;
}

.entrepot-row-numpad__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.entrepot-row-numpad__title {
  flex: 1 1 auto;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #37474f;
}

.entrepot-row-numpad__header-spacer {
  flex: 0 0 40px;
  width: 40px;
}

.entrepot-row-numpad__display {
  margin-bottom: 12px;
  padding: 12px 16px 14px;
  border-radius: 14px;
  border: 1px solid #d5dde8;
  background: linear-gradient(180deg, #f8f9fc 0%, #eef1f6 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85);
}

.entrepot-row-numpad__display-label {
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #8a96a8;
  text-align: right;
}

.entrepot-row-numpad__display-value {
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 32px;
  font-weight: 800;
  color: #5a6b85;
  direction: ltr;
  unicode-bidi: plaintext;
  letter-spacing: 0.03em;
  line-height: 1.1;
}

.entrepot-row-numpad__pad {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 1fr);
  gap: 8px;
  margin-bottom: 10px;
}

.entrepot-row-numpad__digits {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.entrepot-row-numpad__actions {
  display: grid;
  grid-template-rows: repeat(5, minmax(0, 1fr));
  gap: 8px;
}

.entrepot-row-numpad__key {
  min-height: 48px;
  border: 0;
  border-radius: 12px;
  background: #fff;
  color: #263238;
  font-size: 22px;
  font-weight: 800;
  box-shadow: 0 1px 0 rgba(55, 71, 79, 0.08);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.entrepot-row-numpad__key:active {
  background: #eceff1;
}

.entrepot-row-numpad__key--multi {
  font-size: 16px;
}

.entrepot-row-numpad__key--action {
  background: #e8edf5;
  color: #5a6b85;
  font-size: 18px;
  border: 1px solid #d0d8e4;
}

.entrepot-row-numpad__key--action:active {
  background: #d9e1ec;
}

.entrepot-row-numpad__footer {
  margin-top: 2px;
}

.entrepot-row-numpad__confirm {
  width: 100%;
  min-height: 48px;
  border: 1px solid #c5d0e0;
  border-radius: 12px;
  background: #e8edf5;
  color: #5a6b85;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.entrepot-row-numpad__confirm:active {
  background: #d9e1ec;
}

.entrepot-edit-sheet__fields {
  display: grid;
  gap: 12px;
}

.entrepot-edit-sheet__footer {
  padding: 10px 14px 16px;
  border-top: 1px solid #eceff1;
}

.entrepot-edit-sheet__submit {
  border-radius: 8px !important;
}

.entrepot-edit-field-wrap {
  display: grid;
  gap: 8px;
}

.entrepot-edit-field-wrap__label {
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}

.entrepot-edit-picker-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}

.entrepot-edit-picker-field:active:not(:disabled) {
  background: #e3f2fd;
  border-color: #90caf9;
}

.entrepot-edit-picker-field:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.entrepot-edit-picker-field__text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  color: #263238;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.entrepot-cost-add-btn {
  justify-self: start;
  min-height: 32px;
  padding: 0 12px;
  border: 1px solid #90caf9;
  border-radius: 8px;
  background: var(--em-blue-soft);
  color: var(--em-blue);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.entrepot-row-detail {
  direction: rtl;
  border-radius: 16px 16px 0 0 !important;
  max-height: 78vh !important;
  max-height: 78dvh !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.entrepot-row-detail__title {
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  color: #212121;
  padding: 12px 16px 14px;
  border-bottom: 1px solid #eeeeee;
}

.entrepot-row-detail__product {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f2f5;
}

.entrepot-row-detail__media {
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #f0f2f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.entrepot-row-detail__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.entrepot-row-detail__product-text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: right;
}

.entrepot-row-detail__name {
  font-size: 14px;
  font-weight: 700;
  color: #263238;
  text-align: right;
}

.entrepot-row-detail__stock {
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
  line-height: 1.4;
}

.entrepot-row-detail__stock span {
  color: #37474f;
  font-weight: 700;
}

.entrepot-row-detail__change {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e0e4e8;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
}

.entrepot-row-detail__body {
  flex: 1 1 auto;
  min-height: 0;
  max-height: calc(78vh - 220px);
  max-height: calc(78dvh - 220px);
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 12px 16px 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.entrepot-row-detail__body .entrepot-edit-field-wrap {
  margin: 0;
  flex-shrink: 0;
  overflow: visible;
  gap: 8px;
}

.entrepot-row-detail__body .entrepot-edit-field-wrap__label {
  margin: 0 2px;
  position: static;
}

.entrepot-row-detail__input-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}

.entrepot-row-detail__input-row:active:not(:disabled) {
  background: #e3f2fd;
  border-color: #90caf9;
}

.entrepot-row-detail__input-row:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.entrepot-row-detail__affix {
  flex: 0 0 auto;
  color: #78909c;
  font-size: 13px;
  font-weight: 700;
}

.entrepot-row-detail__input {
  flex: 1 1 auto;
  min-width: 0;
}

.entrepot-row-detail__input--static {
  font-size: 15px;
  font-weight: 700;
  color: #263238;
  text-align: left;
  direction: ltr;
  unicode-bidi: plaintext;
}

.entrepot-row-detail__textarea {
  display: block;
  width: 100%;
  min-height: 96px;
  padding: 10px 12px;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  outline: none;
  background: #fff;
  resize: vertical;
  text-align: right;
  font-size: 14px;
  font-weight: 600;
  color: #37474f;
  line-height: 1.5;
  box-sizing: border-box;
  font-family: inherit;
}

.entrepot-row-detail__textarea:focus {
  border-color: #90caf9;
}

.entrepot-row-detail__summary {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px 16px;
  border-top: 1px solid #f0f2f5;
  font-size: 13px;
  font-weight: 700;
  color: #546e7a;
}

.entrepot-row-detail__summary-sep {
  color: #cfd8dc;
}

.entrepot-row-detail__footer {
  flex: 0 0 auto;
  padding: 10px 16px calc(20px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  border-top: 1px solid #eeeeee;
}

.entrepot-edit-sheet__body.entrepot-row-serial-sheet__body {
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
  max-height: min(62vh, 520px) !important;
  padding: 16px 16px 12px !important;
}

.entrepot-row-serial-sheet__add,
.entrepot-row-serial-sheet__toolbar {
  flex: 0 0 auto;
  width: 100%;
  margin: 0 0 16px;
  box-sizing: border-box;
}

.entrepot-row-serial-sheet__list {
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding-bottom: 4px;
}

.entrepot-row-serial-sheet__add {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.entrepot-row-serial-sheet__add-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
}

.entrepot-row-serial-sheet__input,
.entrepot-row-serial-sheet__search-input {
  width: 100%;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid #d7dee6;
  border-radius: 12px;
  background: #fff;
  outline: none;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  color: #37474f;
  text-align: right;
  box-sizing: border-box;
}

.entrepot-row-serial-sheet__input:focus,
.entrepot-row-serial-sheet__search-input:focus {
  border-color: #64b5f6;
}

.entrepot-row-serial-sheet__add-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 80px;
  min-height: 44px;
  padding: 0 12px;
  border: 0;
  border-radius: 12px;
  background: #1976d2;
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}

.entrepot-row-serial-sheet__hint {
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  color: #e65100;
}

.entrepot-row-serial-sheet__search {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 46px;
  margin: 0 0 16px;
  padding: 0 14px;
  border: 1px solid #d7dee6;
  border-radius: 12px;
  background: #fff;
}

.entrepot-row-serial-sheet__search .entrepot-row-serial-sheet__search-input {
  border: 0;
  min-height: 42px;
  padding: 0;
}

.entrepot-row-serial-sheet__tools {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #f3f7fc;
}

.entrepot-row-serial-sheet__link-btn {
  border: 0;
  background: transparent;
  color: #1976d2;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  padding: 0;
  min-width: 64px;
  text-align: center;
}

.entrepot-row-serial-sheet__link-btn.is-muted,
.entrepot-row-serial-sheet__link-btn:disabled {
  color: #b0bec5;
  cursor: default;
}

.entrepot-row-serial-sheet__tools .entrepot-row-serial-sheet__link-btn:first-child {
  justify-self: start;
  text-align: right;
}

.entrepot-row-serial-sheet__tools .entrepot-row-serial-sheet__link-btn:last-child {
  justify-self: end;
  text-align: left;
}

.entrepot-row-serial-sheet__count {
  justify-self: center;
  min-width: 48px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #fff;
  color: #1976d2;
  font-size: 12px;
  font-weight: 800;
  text-align: center;
  box-shadow: 0 0 0 1px rgba(25, 118, 210, 0.12);
}

.entrepot-row-serial-sheet__list > .entrepot-row-serial-sheet__item,
.entrepot-row-serial-sheet__list > .entrepot-row-serial-sheet__empty,
.entrepot-row-serial-sheet__list > .entrepot-row-serial-sheet__more {
  margin: 0 0 16px;
}

.entrepot-row-serial-sheet__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 12px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
}

.entrepot-row-serial-sheet__empty strong {
  color: #607d8b;
  font-size: 13px;
}

.entrepot-row-serial-sheet__item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0;
  min-height: 68px;
  padding: 14px 12px;
  border: 1px solid #e3eaf2;
  border-radius: 14px;
  background: #fff;
  cursor: default;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}

.entrepot-row-serial-sheet__item.is-pickable {
  cursor: pointer;
}

.entrepot-row-serial-sheet__item.is-selected {
  border-color: #90caf9;
  background: #eaf3fc;
  box-shadow: 0 0 0 1px rgba(25, 118, 210, 0.08);
}

.entrepot-row-serial-sheet__side {
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.entrepot-row-serial-sheet__mark {
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.entrepot-row-serial-sheet__mark-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #cfd8dc;
  background: #fff;
  box-sizing: border-box;
}

.entrepot-row-serial-sheet__item.is-selected .entrepot-row-serial-sheet__mark-dot {
  border-color: #1976d2;
  background: #1976d2;
  box-shadow: inset 0 0 0 3px #fff;
}

.entrepot-row-serial-sheet__item-index {
  flex: 0 0 28px;
  width: 28px;
  color: #90a4ae;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
}

.entrepot-row-serial-sheet__item-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;
  padding: 0 4px;
}

.entrepot-row-serial-sheet__item-main--start {
  align-items: flex-start;
  text-align: right;
}

.entrepot-row-serial-sheet__item-main strong {
  font-size: 14px;
  font-weight: 800;
  color: #37474f;
  word-break: break-all;
  line-height: 1.35;
}

.entrepot-row-serial-sheet__item-main small {
  font-size: 11px;
  font-weight: 600;
  color: #90a4ae;
}

.entrepot-row-serial-sheet__remove {
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.entrepot-row-serial-sheet__more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 44px;
  border: 1px dashed #c5d6ea;
  border-radius: 12px;
  background: #f5f9fd;
  color: #1976d2;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.entrepot-row-serial-sheet__more:disabled {
  opacity: 0.6;
  cursor: default;
}

.entrepot-review__hint {
  margin: 0 2px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
  line-height: 1.7;
}

.entrepot-review__card {
  padding: 14px;
  border: 1px solid #e8eef3;
  border-radius: 10px;
  background: #fafbfd;
}

.entrepot-review__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 13px;
  color: #78909c;
}

.entrepot-review__row strong {
  color: #263238;
  font-weight: 700;
  text-align: left;
}

.entrepot-review__list-btn {
  width: 100%;
  margin-top: 12px;
  min-height: 44px;
  border: 1px solid #e0e4e8;
  border-radius: 8px;
  background: #fff;
  color: #455a64;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.entrepot-more-sheet__body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 10px 18px;
}

.entrepot-more-sheet__item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 48px;
  padding: 10px 12px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #263238;
  font-size: 14px;
  font-weight: 600;
  text-align: start;
  cursor: pointer;
}

.entrepot-more-sheet__excel {
  width: 100%;
}

.entrepot-content-sheet__textarea {
  display: block;
  width: 100%;
  min-height: 96px;
  padding: 10px 12px;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  outline: none;
  background: #fff;
  resize: vertical;
  text-align: right;
  font-size: 14px;
  font-weight: 600;
  color: #37474f;
  line-height: 1.5;
  box-sizing: border-box;
  font-family: inherit;
}

.entrepot-content-sheet__textarea:focus {
  border-color: #90caf9;
}

.entrepot-status-tag-sheet {
  display: flex !important;
  flex-direction: column !important;
  overflow: visible !important;
  min-height: 320px;
}

.entrepot-status-tag-sheet .entrepot-edit-sheet__header {
  flex: 0 0 auto;
}

.entrepot-status-tag-sheet__body {
  flex: 1 1 auto !important;
  max-height: none !important;
  overflow: visible !important;
  padding: 16px 16px 8px !important;
}

.entrepot-status-tag-sheet__body .entrepot-edit-sheet__fields {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.entrepot-status-tag-sheet__body .entrepot-edit-field-wrap {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  position: relative;
}

.entrepot-status-tag-sheet__body .entrepot-edit-field-wrap__label {
  position: relative;
  z-index: 1;
  margin: 0 2px 10px;
  font-size: 13px;
  font-weight: 600;
  color: #455a64;
  line-height: 1.4;
}

.entrepot-status-tag-sheet .entrepot-edit-sheet__footer {
  flex: 0 0 auto;
  margin-top: 8px;
  padding: 12px 16px calc(18px + env(safe-area-inset-bottom, 0px));
}

.entrepot-status-tag-sheet__field {
  display: block;
  width: 100%;
  min-height: 44px;
}

/* Status root v-container otherwise collapses (height=32) and overlaps the next label */
.entrepot-status-tag-sheet__status {
  min-height: 44px;
  margin-bottom: 2px;
}

.entrepot-status-tag-sheet__status ::v-deep > .container,
.entrepot-status-tag-sheet__status ::v-deep > .v-container {
  padding: 0 !important;
  max-width: none !important;
  min-height: 44px !important;
  height: 44px !important;
  overflow: visible !important;
}

.entrepot-status-tag-sheet__field ::v-deep > .container,
.entrepot-status-tag-sheet__field ::v-deep > .v-container {
  padding: 0 !important;
  max-width: none !important;
}

.entrepot-status-tag-sheet__field ::v-deep .v-input {
  margin: 0 !important;
  padding: 0 !important;
  width: 100% !important;
  min-height: 44px !important;
}

.entrepot-status-tag-sheet__field ::v-deep .v-input__control {
  min-height: 44px !important;
}

.entrepot-status-tag-sheet__field ::v-deep .v-input__slot {
  min-height: 44px !important;
  height: 44px !important;
  margin: 0 !important;
  padding: 0 12px !important;
  border: 1px solid #cfd8dc !important;
  border-radius: 8px !important;
  background: #fff !important;
  box-shadow: none !important;
}

.entrepot-status-tag-sheet__tag ::v-deep .v-input__slot {
  height: auto !important;
  min-height: 44px !important;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

.entrepot-status-tag-sheet__field ::v-deep fieldset {
  border: 0 !important;
}

.entrepot-status-tag-sheet__field ::v-deep .v-input__slot::before,
.entrepot-status-tag-sheet__field ::v-deep .v-input__slot::after {
  display: none !important;
}

.entrepot-status-tag-sheet__field ::v-deep .v-label {
  display: none !important;
}

.entrepot-status-tag-sheet__field ::v-deep .v-text-field__details,
.entrepot-status-tag-sheet__field ::v-deep .v-messages {
  display: none !important;
  min-height: 0 !important;
}

.entrepot-status-tag-sheet__field ::v-deep .v-icon {
  color: #78909c !important;
  font-size: 20px !important;
}

.entrepot-status-tag-sheet__field ::v-deep .v-select__selections {
  min-height: 42px !important;
  padding: 4px 0 !important;
  flex-wrap: nowrap;
}

.entrepot-status-tag-sheet__field ::v-deep .v-chip {
  margin: 0 0 0 4px !important;
}

.entrepot-edit-sheet__body.entrepot-documents-sheet__body {
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
  max-height: min(62vh, 480px);
}

.entrepot-documents-sheet__hint {
  flex: 0 0 auto;
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
  line-height: 1.5;
}

.entrepot-documents-sheet__picker {
  flex: 1 1 auto !important;
  min-height: 0 !important;
  width: 100% !important;
  margin: 0 0 12px !important;
  overflow: hidden !important;
  background: transparent !important;
  display: flex !important;
  flex-direction: column !important;
  box-shadow: none !important;
}

.entrepot-documents-sheet__picker ::v-deep > .v-form {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.entrepot-documents-sheet__picker ::v-deep > .v-form > .v-btn {
  flex: 0 0 auto;
  min-height: 48px !important;
  height: 48px !important;
  margin: 0 0 12px !important;
  border-radius: 12px !important;
  border: 1px dashed #90a4ae !important;
  background: #f5f7f9 !important;
  color: #546e7a !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
}

.entrepot-documents-sheet__picker ::v-deep .v-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  background: transparent !important;
  padding: 0 !important;
}

.entrepot-documents-sheet__picker ::v-deep .v-list-item {
  min-height: 64px !important;
  margin: 0 0 10px !important;
  padding: 10px 12px !important;
  border: 1px solid #e0e4e8 !important;
  border-radius: 12px !important;
  background: #fff !important;
}

.entrepot-documents-sheet__picker ::v-deep .v-list-item__avatar {
  margin: 0 0 0 12px !important;
  border-radius: 10px !important;
  overflow: hidden;
}

.entrepot-documents-sheet__picker ::v-deep .v-list-item__title {
  font-size: 14px !important;
  font-weight: 700 !important;
  color: #263238 !important;
  text-align: right;
}

.entrepot-documents-sheet__picker ::v-deep .v-list-item__subtitle {
  font-size: 12px !important;
  color: #90a4ae !important;
  text-align: right;
}

.entrepot-documents-sheet__picker ::v-deep .v-list-item__icon .v-icon {
  color: #e91e63 !important;
}

.entrepot-mobile ::v-deep .v-input {
  height: auto !important;
  margin: 0;
  padding: 0;
}

.entrepot-entity-picker {
  --picker-nude: #eceff2;
  --picker-nude-border: #c5ced6;
  --picker-nude-strong: #607d8b;
  --picker-nude-soft: #f3f5f7;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100vh;
  background: var(--picker-nude-soft) !important;
  border-radius: 0 !important;
}

.entrepot-entity-picker__header {
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: 44px 1fr auto;
  align-items: center;
  gap: 4px;
  min-height: 56px;
  padding: 8px 10px;
  background: #fff;
  border-bottom: 1px solid #eceff1;
}

.entrepot-entity-picker__title {
  margin: 0;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  color: #263238;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.entrepot-entity-picker__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 44px;
}

.entrepot-entity-picker__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.entrepot-entity-picker__icon-btn:active {
  background: var(--picker-nude);
}

.entrepot-entity-picker__search {
  flex: 0 0 auto;
  padding: 12px;
  background: #fff;
}

.entrepot-entity-picker__search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  min-height: 48px;
  padding: 0 12px;
  border-radius: 10px;
  background: var(--picker-nude);
  border: 1px solid #e0e4e8;
  box-sizing: border-box;
}

.entrepot-entity-picker__search-icon {
  flex: 0 0 auto;
}

.entrepot-entity-picker__search-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 15px;
  font-weight: 500;
  color: #263238;
  text-align: right;
  line-height: 1.4;
}

.entrepot-entity-picker__search-input::placeholder {
  color: #90a4ae;
  font-weight: 400;
}

.entrepot-entity-picker__search-input::-webkit-search-cancel-button {
  display: none;
}

.entrepot-entity-picker__search-clear {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
}

.entrepot-entity-picker__divider {
  flex: 0 0 auto;
  position: relative;
  height: 1px;
  background: #eceff1;
}

.entrepot-entity-picker__loader {
  position: absolute !important;
  left: 0 !important;
  right: 0 !important;
  top: 0 !important;
  margin: 0 !important;
}

.entrepot-entity-picker__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 10px 12px 20px;
}

.entrepot-entity-picker__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 52px;
  margin: 0 0 8px;
  padding: 10px 12px;
  border: 1px solid #e8eef3;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  text-align: right;
}

.entrepot-entity-picker__item--active {
  border-color: var(--picker-nude-border);
  background: var(--picker-nude);
}

.entrepot-entity-picker__index {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border-radius: 6px;
  background: #e8ecef;
  color: #607d8b;
  font-size: 12px;
  font-weight: 700;
}

.entrepot-entity-picker__item-main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.entrepot-entity-picker__item-title {
  font-size: 14px;
  font-weight: 600;
  color: #263238;
}

.entrepot-entity-picker__item-sub {
  font-size: 12px;
  color: #78909c;
}

.entrepot-entity-picker__more {
  display: flex;
  justify-content: center;
  padding: 12px 0;
  min-height: 40px;
}

.entrepot-entity-picker__empty {
  text-align: center;
  font-size: 13px;
  color: #90a4ae;
  padding: 28px 0;
}
</style>
